import React, { useState, useEffect, useRef } from 'react';
import usePersistedTab from '../hooks/usePersistedTab';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Tabs,
  Tab,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  IconButton,
  TablePagination,
  Collapse,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Refresh,
  Casino,
  BarChart,
  PlayCircle,
  DeleteForever,
  People,
  FlightTakeoff,
  BubbleChart,
  RocketLaunch,
  SportsEsports,
  EmojiEvents,
  TrendingUp,
  TrendingDown,
  Insights,
  ArrowUpward,
  ArrowDownward,
  KeyboardArrowDown,
  KeyboardArrowUp,
  CalendarMonth,
  NavigateBefore,
  NavigateNext,
  Campaign,
  GpsFixed
} from '@mui/icons-material';
import axios from 'axios';
import SlotPlayerGambleMenu from '../components/SlotPlayerGambleMenu';
import AviatorAdmin from './AviatorAdmin';
import WiggleAdmin from './WiggleAdmin';
import PlinkoAdmin from './PlinkoAdmin';
import LadderAdmin from './LadderAdmin';
import SugarStrikeAdmin from './SugarStrikeAdmin';

// 🌙 Kaguya Ways (game_id 9) — подписи для Live: путь «Выбора принцессы» и мини-фичи героинь
const KG_PATH = { long: '🌸 ДОЛГАЯ НОЧЬ', golden: '🌸 ЗОЛОТАЯ НОЧЬ', danger: '🌸 ОПАСНАЯ НОЧЬ' };
const KG_MINI = { moonlight: '🌙 КАГУЯ', shuriken: '✴️ КУНОИЧИ', fan: '🪭 ГЕЙША', katana: '⚔️ САМУРАЙ', fox: '🦊 КИЦУНЭ' };

// API базовый URL для слотов (отдельный backend на порту 5010)
const SLOTS_API = 'https://slots.moonraid.online/api/admin/slots';

// 15.09.2026: у голого axios таймаута не было — зависший GET висел вечно,
// спиннер крутился без ошибки. Отдельный инстанс ТОЛЬКО для чтения (GET):
// мутации (сброс статы, публикация уведомления) намеренно идут мимо него —
// оборвать POST/PUT/DELETE по таймауту опасно, запрос мог уже примениться
// на сервере, а юзер увидит ошибку и нажмёт второй раз.
// 90с > 60с proxy_read_timeout у nginx для slots.moonraid.online, то есть
// сработает только когда nginx вообще замолчал. Ничего живого не рубит:
// самый долгий GET тут — /profit-history, 31с (полный агрегат за всё время).
const slotsGet = axios.create({ timeout: 90000 });

// Индекс вкладки «Уведомление» (последняя). Держим одной константой:
// от него зависит ленивая загрузка данных вкладки.
const ANN_TAB_INDEX = 9;

// 🔒 Функция для получения заголовков авторизации
const getAuthHeaders = () => {
  const token = localStorage.getItem('adminToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

// Разбивка профита по слотам и источникам выигрыша (обычные / фриспины / бонус / ante).
// Главное: помочь админу понять, ОТКУДА юзер приумножил баланс.
// Человекочитаемые названия бонус-фич (slot-specific).
const BONUS_TYPE_LABELS = {
  free_spins_expanding: 'Фриспины (Expanding)',
  star_duel: 'Star Duel',
  dead_star: 'Dead Star',
  train_heist: 'Train Heist',
  free_spins: 'Фриспины',
  buy_bonus: 'Buy Bonus'
};
const formatBonusType = (t) => BONUS_TYPE_LABELS[t] || t || 'Фриспины';

// §24.07 Sugar Strike: цена выстрела = ставка·costMult (0.12 на тяжёлой пушке → 0.18, а на
// средней → 0.132). Два знака такую цену округляют и снова врут, поэтому третий знак
// показываем только когда он есть.
const ssMoney = (v) => {
  const n = Number(v) || 0;
  return Math.abs(n * 100 - Math.round(n * 100)) < 1e-6 ? n.toFixed(2) : n.toFixed(3);
};

const ProfitBreakdown = ({ gameStats }) => {
  // показываем только слоты, где юзер реально в плюсе
  const winning = gameStats.filter(gs => (gs.profit || 0) > 0);

  // считаем суммарный профит из обычных и из бонус-раундов по выигрышным слотам
  const totals = winning.reduce(
    (acc, gs) => {
      acc.profit += gs.profit || 0;
      acc.profitNormal += gs.profitNormal || 0;
      acc.profitFree += gs.profitFree || 0;
      acc.profitBuy += gs.profitBuyBonus || 0;
      acc.profitAnte += gs.profitAnte || 0;
      acc.spins += gs.spins || 0;
      return acc;
    },
    { profit: 0, profitNormal: 0, profitFree: 0, profitBuy: 0, profitAnte: 0, spins: 0 }
  );

  const fmt = (n) => `${n > 0 ? '+' : ''}${n.toFixed(2)}`;
  const profitColor = (n) => (n > 0 ? '#4caf50' : n < 0 ? '#f44336' : 'inherit');

  // отдельная плитка-источник профита
  const SourceTile = ({ label, value, hint, color }) => (
    <Grid item xs={6} sm={3}>
      <Paper sx={{ p: 1.2, textAlign: 'center', bgcolor: 'rgba(30, 41, 59, 0.6)' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
        <Typography variant="body1" fontWeight="bold" sx={{ color: color || profitColor(value) }}>
          {fmt(value)}
        </Typography>
        {hint && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{hint}</Typography>
        )}
      </Paper>
    </Grid>
  );

  // если ни одного выигрышного слота — короткое сообщение
  if (winning.length === 0) {
    return (
      <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: 'rgba(244,67,54,0.08)', border: '1px solid rgba(244,67,54,0.25)' }}>
        <Typography variant="body2" color="text.secondary">
          Этот юзер ни в одном слоте не в плюсе — все слоты убыточные.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      {/* Шапка: итоговый профит только по выигрышным слотам */}
      <Box sx={{
        mb: 1.5, p: 1.5, borderRadius: 2,
        background: 'linear-gradient(135deg, rgba(76,175,80,0.15) 0%, rgba(76,175,80,0.05) 100%)',
        border: '1px solid rgba(76,175,80,0.35)'
      }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
          Где юзер в плюсе сегодня — итог: <span style={{ color: profitColor(totals.profit) }}>{fmt(totals.profit)} SPERM</span>
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            (за сегодня с 00:00 МСК, только прибыльные слоты)
          </Typography>
        </Typography>
        <Grid container spacing={1}>
          <SourceTile label="Обычные спины" value={totals.profitNormal} hint="база (без бонус-раунда)" />
          <SourceTile label="Фриспины (бонус)" value={totals.profitFree} hint="выигрыши в бонус-раунде" color="#4caf50" />
          <SourceTile label="Покупка бонуса" value={totals.profitBuy} hint="buy bonus" />
          <SourceTile label="Ante bet" value={totals.profitAnte} hint="ante режим" />
        </Grid>
      </Box>

      {/* По каждому выигрышному слоту: общая строка + расшифровка по типу бонус-фичи */}
      <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>
        Прибыльные слоты сегодня — где именно плюсанул:
      </Typography>
      <Table size="small" sx={{ bgcolor: 'rgba(30, 41, 59, 0.3)' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ py: 0.5 }}>Слот / источник</TableCell>
            <TableCell align="right" sx={{ py: 0.5 }}>Спинов</TableCell>
            <TableCell align="right" sx={{ py: 0.5 }}>Ставки</TableCell>
            <TableCell align="right" sx={{ py: 0.5 }}>Выигрыши</TableCell>
            <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold' }}>Профит</TableCell>
            <TableCell align="center" sx={{ py: 0.5 }}>Триггеров</TableCell>
            <TableCell align="right" sx={{ py: 0.5 }}>Макс. вин</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {winning.map((gs) => {
            // только прибыльные подкатегории внутри слота
            const subRows = [];
            if (gs.profitNormal > 0) {
              subRows.push({
                key: 'normal', label: 'База (обычные спины)',
                spins: gs.spinsNormal, bets: gs.betsNormal,
                wins: gs.winsNormal, profit: gs.profitNormal
              });
            }
            if (gs.profitFree > 0) {
              // если есть детализация по типам бонусов — раскрываем по ним,
              // иначе одной строкой "Фриспины"
              const winningBonus = (gs.bonusBreakdown || []).filter(b => b.profit > 0);
              if (winningBonus.length > 0) {
                winningBonus.forEach(b => subRows.push({
                  key: `bonus-${b.bonusType}`,
                  label: `└ ${formatBonusType(b.bonusType)}`,
                  spins: b.spins, bets: b.bets, wins: b.wins, profit: b.profit,
                  highlight: true
                }));
              } else {
                subRows.push({
                  key: 'free', label: 'Фриспины (бонус-раунд)',
                  spins: gs.spinsFree, bets: 0, wins: gs.winsFree, profit: gs.profitFree,
                  highlight: true
                });
              }
            }
            if (gs.profitBuyBonus > 0) {
              subRows.push({
                key: 'buy', label: 'Покупка бонуса',
                spins: gs.spinsBuyBonus, bets: gs.betsBuyBonus,
                wins: gs.winsBuyBonus, profit: gs.profitBuyBonus
              });
            }
            if (gs.profitAnte > 0) {
              subRows.push({
                key: 'ante', label: 'Ante bet',
                spins: gs.spinsAnte, bets: gs.betsAnte,
                wins: gs.winsAnte, profit: gs.profitAnte
              });
            }
            return (
              <React.Fragment key={gs.gameId}>
                {/* Заголовок слота — общая строка */}
                <TableRow sx={{ bgcolor: 'rgba(76,175,80,0.08)' }}>
                  <TableCell sx={{ py: 0.5, fontWeight: 'bold' }}>{gs.displayName || gs.gameName}</TableCell>
                  <TableCell align="right" sx={{ py: 0.5 }}>{gs.spins}</TableCell>
                  <TableCell align="right" sx={{ py: 0.5 }}>{gs.bets.toFixed(2)}</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, color: '#4caf50' }}>{gs.wins.toFixed(2)}</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold', color: profitColor(gs.profit) }}>
                    {fmt(gs.profit)}
                  </TableCell>
                  <TableCell align="center" sx={{ py: 0.5 }}>
                    {gs.bonusTriggers > 0
                      ? <Chip label={gs.bonusTriggers} size="small" color="warning" sx={{ height: 18, fontSize: '0.7rem' }} />
                      : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5 }}>
                    {gs.maxWin > 0 ? gs.maxWin.toFixed(2) : '—'}
                  </TableCell>
                </TableRow>
                {/* Расшифровка — где именно набежал плюс */}
                {subRows.map(r => (
                  <TableRow key={`${gs.gameId}-${r.key}`}>
                    <TableCell sx={{ py: 0.4, pl: 3, color: r.highlight ? '#ffc107' : 'text.secondary', fontSize: '0.82rem' }}>
                      {r.label}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.4, fontSize: '0.82rem' }}>{r.spins}</TableCell>
                    <TableCell align="right" sx={{ py: 0.4, fontSize: '0.82rem' }}>{r.bets.toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ py: 0.4, color: '#4caf50', fontSize: '0.82rem' }}>{r.wins.toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ py: 0.4, fontWeight: 'bold', color: profitColor(r.profit), fontSize: '0.82rem' }}>
                      {fmt(r.profit)}
                    </TableCell>
                    <TableCell sx={{ py: 0.4 }} />
                    <TableCell sx={{ py: 0.4 }} />
                  </TableRow>
                ))}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

// 🎯 Объективный P&L по юзеру (касса). Главное число: занёс − вывел −
// что ещё может забрать. + (зелёный) = казино в плюсе с юзера, − (красный) =
// мы слили ему. Не зависит от лога вагеров (он неполный по легаси).
const CashTruthBlock = ({ user }) => {
  if (!user || user.realPnLCasino === undefined || user.realPnLCasino === null) return null;
  const ct = user.cashTruth || {};
  const pnl = user.realPnLCasino;
  const color = pnl > 0 ? '#4caf50' : pnl < 0 ? '#f44336' : 'inherit';
  const fmt = (n) => (Number(n) || 0).toFixed(2);
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block', color: '#6366f1' }}>
        🎯 Реальный P&L (касса) — объективно
      </Typography>
      <Paper sx={{ p: 1.5, bgcolor: pnl >= 0 ? 'rgba(76,175,80,0.08)' : 'rgba(244,67,54,0.08)', mb: 1 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ color, mb: 1 }}>
          {pnl > 0 ? '+' : ''}{fmt(pnl)} SPERM
          <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
            {pnl >= 0 ? 'казино в плюсе' : 'мы слили юзеру'}
          </Typography>
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">Занёс</Typography>
            <Typography variant="body2" fontWeight="bold">{fmt(ct.deposited)}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">Вывел</Typography>
            <Typography variant="body2" fontWeight="bold" sx={{ color: '#f44336' }}>{fmt(ct.withdrawn)}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">Ещё может забрать</Typography>
            <Typography variant="body2" fontWeight="bold">{fmt(ct.withdrawable)}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
              баланс {fmt(ct.freeBalance)} · копилка {fmt(ct.piggy)} · заявки и звёздный стейк {fmt(ct.owed)}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">В локе (вагер)</Typography>
            <Typography variant="body2" fontWeight="bold" sx={{ color: '#ff9800' }}>{fmt(ct.locked)}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">💎 Кэшбэк в стэше</Typography>
            <Typography variant="body2" fontWeight="bold" sx={{ color: '#9c27b0' }}>{fmt(ct.stashBalance)}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">💎 Лоялти на руках (стэш+лок)</Typography>
            <Typography variant="body2" fontWeight="bold" sx={{ color: '#9c27b0' }}>{fmt((Number(ct.stashBalance)||0) + (Number(ct.locked)||0))}</Typography>
          </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Формула: занёс (TON и SPERM, вкл. стейк за TON) − вывел − что ещё может забрать.
          Лоялти в локе и стэше не считаем, пока не отыграно. Кэшбэка получено всего: {fmt(ct.cashbackClaimed)} SPERM
        </Typography>
      </Paper>
    </Box>
  );
};

const SlotsSettings = () => {
  const [tabValue, setTabValue] = usePersistedTab('slots', 0); // Статистика первая по умолчанию
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Данные
  const [stats, setStats] = useState(null);
  const [sugarStats, setSugarStats] = useState(null); // §владелец 13.07: Sugar Strike (USDT) строкой в «RTP по играм»
  const [liveSpins, setLiveSpins] = useState([]);

  // 📢 Важное уведомление в лобби (модалка поверх всего)
  const [annForm, setAnnForm] = useState({
    title: '', body: '', linkUrl: '', linkText: '', enabled: false, testOnly: true
  });
  const [annMeta, setAnnMeta] = useState(null); // { version, updated_at }
  const [annLoaded, setAnnLoaded] = useState(false);
  const [annSaving, setAnnSaving] = useState(false);
  // 📬 История рассылок: охват / нажатия OK + раскрытый список юзеров
  const [annBroadcasts, setAnnBroadcasts] = useState([]);
  const [annBroadcastsTotal, setAnnBroadcastsTotal] = useState(0);
  const [annBroadcastsPage, setAnnBroadcastsPage] = useState(0);
  const [annAcksVersion, setAnnAcksVersion] = useState(null);
  const [annAcks, setAnnAcks] = useState([]);
  const [annAcksTotal, setAnnAcksTotal] = useState(0);
  const [annAcksPage, setAnnAcksPage] = useState(0);
  // 👤 Личное уведомление одному игроку (та же модалка в лобби, но адресно)
  const [persForm, setPersForm] = useState({ title: '', body: '', linkUrl: '', linkText: '' });
  const [persQuery, setPersQuery] = useState('');
  const [persFound, setPersFound] = useState([]);
  const [persUser, setPersUser] = useState(null);
  const [persSending, setPersSending] = useState(false);
  const [persList, setPersList] = useState([]);
  const [persTotal, setPersTotal] = useState(0);
  const [persPage, setPersPage] = useState(0);

  // Пользователи слотов
  const [slotUsers, setSlotUsers] = useState([]);
  const [slotUsersTotal, setSlotUsersTotal] = useState(0);
  const [jackpotWinners, setJackpotWinners] = useState([]);
  const [currentJackpotInfo, setCurrentJackpotInfo] = useState(null);
  const [editingCooldown, setEditingCooldown] = useState(false);
  const [cooldownInput, setCooldownInput] = useState('');
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [userDetails, setUserDetails] = useState(null);

  // v26.02.08: Gamble лидерборд
  const [gambleLeaderboard, setGambleLeaderboard] = useState([]);

  // v25.01.24: Последние бонусных игр с пагинацией (для вкладки Статистика)
  const [recentBonuses, setRecentBonuses] = useState([]);
  const [bonusPage, setBonusPage] = useState(1);
  const [bonusTotalPages, setBonusTotalPages] = useState(1);
  const bonusPageRef = useRef(1); // Ref для актуального номера страницы (fix stale closure в interval/SSE)
  const [bonusGameFilter, setBonusGameFilter] = useState(''); // Фильтр по игре
  const bonusGameFilterRef = useRef(''); // Ref для актуального фильтра

  // v25.01.14: Онлайн игроки (активные за последние 5 минут)
  const [onlinePlayers, setOnlinePlayers] = useState(0);

  // v26.02.07: История профита по дням + сводка
  const [profitHistory, setProfitHistory] = useState([]);
  const [profitHistoryPage, setProfitHistoryPage] = useState(0);
  const [profitHistoryTotalPages, setProfitHistoryTotalPages] = useState(1);
  const [profitSummaries, setProfitSummaries] = useState(null);
  // v26.02.07: Топ игроков по дням
  const [topPlayersByDate, setTopPlayersByDate] = useState([]); // минуснули нас
  const [topProfitPlayers, setTopProfitPlayers] = useState([]); // принесли прибыль
  const [dayTotal, setDayTotal] = useState(null); // суммарный профит за сутки
  const [topPlayersDate, setTopPlayersDate] = useState(null); // null = сегодня
  const [topPlayersAvailableDays, setTopPlayersAvailableDays] = useState([]);
  const [topPlayersDayIndex, setTopPlayersDayIndex] = useState(0); // 0 = сегодня
  // Пока запрос в полёте — крутим спиннер. Раньше пустой массив на старте
  // рисовал «Нет игроков за выбранный день», и вкладка выглядела сломанной.
  const [topPlayersLoading, setTopPlayersLoading] = useState(true);
  // Упавший запрос тоже не должен маскироваться под «игроков нет».
  const [topPlayersError, setTopPlayersError] = useState(null);

  // v26.07.01: тяжёлая аналитика (Все игроки + профит по дням) грузится лениво
  const [analyticsLoaded, setAnalyticsLoaded] = useState(false);

  // Gamble Menu модалка
  const [gambleMenuOpen, setGambleMenuOpen] = useState(false);
  const [gambleMenuUserId, setGambleMenuUserId] = useState(null);

  const openGambleMenu = (userId) => {
    setGambleMenuUserId(userId);
    setGambleMenuOpen(true);
  };

  // v17.10: Сортировка пользователей
  // По умолчанию — объективный P&L (касса), по возрастанию: фармеры
  // (мы в минусе с них) сверху страницы.
  const [sortField, setSortField] = useState('realPnlCasino');
  const [sortDirection, setSortDirection] = useState('asc');

  // v26.01.26: Пагинация для таблицы "Все игроки"
  const [usersPage, setUsersPage] = useState(0);
  const [usersPerPage, setUsersPerPage] = useState(50);

  // v17.10: Функция переключения сортировки
  const handleSort = (field) => {
    if (sortField === field) {
      // Если уже сортируем по этому полю, меняем направление
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Новое поле - начинаем с desc
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // v17.10: Отсортированный список пользователей
  const sortedUsers = [...slotUsers].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];

    // Обработка строковых полей
    if (sortField === 'displayName' || sortField === 'telegramId') {
      aVal = String(aVal || '').toLowerCase();
      bVal = String(bVal || '').toLowerCase();
      if (sortDirection === 'asc') {
        return aVal.localeCompare(bVal);
      } else {
        return bVal.localeCompare(aVal);
      }
    }

    // Обработка дат
    if (sortField === 'lastPlayed') {
      aVal = aVal ? new Date(aVal).getTime() : 0;
      bVal = bVal ? new Date(bVal).getTime() : 0;
    }

    // Числовая сортировка
    aVal = Number(aVal) || 0;
    bVal = Number(bVal) || 0;

    if (sortDirection === 'asc') {
      return aVal - bVal;
    } else {
      return bVal - aVal;
    }
  });

  // v17.10: Компонент сортируемого заголовка
  const SortableHeader = ({ field, children, align = 'left' }) => (
    <TableCell
      align={align}
      onClick={() => handleSort(field)}
      sx={{
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.1)' },
        whiteSpace: 'nowrap'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: align === 'right' ? 'flex-end' : 'flex-start', gap: 0.5 }}>
        {children}
        {sortField === field && (
          sortDirection === 'asc'
            ? <ArrowUpward sx={{ fontSize: 16, color: '#6366f1' }} />
            : <ArrowDownward sx={{ fontSize: 16, color: '#6366f1' }} />
        )}
      </Box>
    </TableCell>
  );

  // Загрузка всех данных
  const loadData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const statsRes = await slotsGet.get(`${SLOTS_API}/stats`, { headers });
      setStats(statsRes.data);
      // §владелец 13.07: Sugar Strike (USDT, отдельный бэк) — строкой в общий список «RTP по играм»
      try {
        const ss = await slotsGet.get('https://slots.moonraid.online/api/admin/sugar/stats', { headers });
        if (ss.data?.success) setSugarStats(ss.data);
      } catch (e) { /* SS необязательна — не рушим общий экран статистики */ }
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
      setSnackbar({ open: true, message: 'Ошибка загрузки данных', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Загрузка live данных
  const loadLiveData = async () => {
    try {
      const res = await slotsGet.get(`${SLOTS_API}/live?limit=100`, { headers: getAuthHeaders() });
      setLiveSpins(res.data.spins || []);
    } catch (error) {
      console.error('Ошибка загрузки live данных:', error);
    }
  };

  // Загрузка пользователей слотов с пагинацией
  const loadSlotUsers = async (page = usersPage, perPage = usersPerPage) => {
    try {
      const offset = page * perPage;
      const res = await slotsGet.get(`${SLOTS_API}/users?limit=${perPage}&offset=${offset}`, { headers: getAuthHeaders() });
      setSlotUsers(res.data.users || []);
      setSlotUsersTotal(res.data.total || 0);
    } catch (error) {
      console.error('Ошибка загрузки пользователей:', error);
    }
  };

  // Обработчик смены страницы
  const handleUsersPageChange = (event, newPage) => {
    setUsersPage(newPage);
    loadSlotUsers(newPage, usersPerPage);
  };

  // Обработчик смены количества строк на странице
  const handleUsersPerPageChange = (event) => {
    const newPerPage = parseInt(event.target.value, 10);
    setUsersPerPage(newPerPage);
    setUsersPage(0);
    loadSlotUsers(0, newPerPage);
  };

  // Загрузка победителей джекпотов
  const loadJackpotWinners = async () => {
    try {
      const res = await slotsGet.get(`${SLOTS_API}/jackpot-winners`, { headers: getAuthHeaders() });
      setJackpotWinners(res.data.winners || []);
      setCurrentJackpotInfo(res.data.currentJackpot);
    } catch (error) {
      console.error('Ошибка загрузки джекпотов:', error);
    }
  };

  // Сохранение кулдауна джекпота
  const saveJackpotCooldown = async () => {
    const value = parseInt(cooldownInput);
    if (isNaN(value) || value < 0) {
      setSnackbar({ open: true, message: 'Введите число >= 0', severity: 'error' });
      return;
    }
    try {
      await axios.post(`${SLOTS_API}/jackpot-cooldown`, { cooldownSpins: value }, { headers: getAuthHeaders() });
      setCurrentJackpotInfo(prev => prev ? { ...prev, cooldownSpins: value } : prev);
      setEditingCooldown(false);
      setSnackbar({ open: true, message: `КД джекпота установлен: ${value.toLocaleString()} спинов`, severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Ошибка: ' + (error.response?.data?.error || error.message), severity: 'error' });
    }
  };

  // 📢 Загрузка уведомления лобби
  const loadAnnouncement = async () => {
    try {
      const res = await slotsGet.get(`${SLOTS_API}/lobby-announcement`, { headers: getAuthHeaders() });
      const a = res.data.announcement;
      if (a) {
        setAnnForm({
          title: a.title || '',
          body: a.body || '',
          linkUrl: a.link_url || '',
          linkText: a.link_text || '',
          enabled: !!a.enabled,
          testOnly: a.test_only !== false
        });
        setAnnMeta({ version: a.version, updatedAt: a.updated_at });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Ошибка загрузки уведомления', severity: 'error' });
    } finally {
      // Форму показываем в любом случае: иначе при сбое вкладка вечно крутит спиннер
      setAnnLoaded(true);
    }
  };

  // 📬 История рассылок (пагинация по 10)
  const loadAnnBroadcasts = async (page = 0) => {
    try {
      const res = await slotsGet.get(
        `${SLOTS_API}/lobby-announcement/broadcasts?page=${page}&limit=10`,
        { headers: getAuthHeaders() }
      );
      setAnnBroadcasts(res.data.broadcasts || []);
      setAnnBroadcastsTotal(res.data.total || 0);
      setAnnBroadcastsPage(page);
    } catch (error) {
      console.error('Ошибка загрузки истории рассылок:', error);
    }
  };

  // 📬 Кто нажал OK на рассылке (пагинация по 10)
  const loadAnnAcks = async (version, page = 0) => {
    try {
      const res = await slotsGet.get(
        `${SLOTS_API}/lobby-announcement/broadcasts/${version}/acks?page=${page}&limit=10`,
        { headers: getAuthHeaders() }
      );
      setAnnAcksVersion(version);
      setAnnAcks(res.data.acks || []);
      setAnnAcksTotal(res.data.total || 0);
      setAnnAcksPage(page);
    } catch (error) {
      console.error('Ошибка загрузки OK-статистики:', error);
    }
  };

  // 👤 Личные уведомления: история (пагинация по 10)
  const loadPersonalNotices = async (page = 0) => {
    try {
      const res = await slotsGet.get(
        `${SLOTS_API}/lobby-announcement/personal?page=${page}&limit=10`,
        { headers: getAuthHeaders() }
      );
      setPersList(res.data.notices || []);
      setPersTotal(res.data.total || 0);
      setPersPage(page);
    } catch (error) {
      console.error('Ошибка загрузки личных уведомлений:', error);
    }
  };

  // 👤 Поиск игрока: @username, telegram_id или внутренний id
  const searchPersonalUser = async () => {
    const q = persQuery.trim();
    if (q.length < 2) {
      setSnackbar({ open: true, message: 'Введи минимум 2 символа', severity: 'error' });
      return;
    }
    try {
      const res = await slotsGet.get(
        `${SLOTS_API}/lobby-announcement/personal/search?q=${encodeURIComponent(q)}`,
        { headers: getAuthHeaders() }
      );
      setPersFound(res.data.users || []);
      if ((res.data.users || []).length === 0) {
        setSnackbar({ open: true, message: 'Игрок не найден', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Ошибка поиска: ' + (error.response?.data?.error || error.message), severity: 'error' });
    }
  };

  // 👤 Отправка личного уведомления выбранному игроку
  const sendPersonalNotice = async () => {
    if (!persUser) {
      setSnackbar({ open: true, message: 'Сначала выбери игрока', severity: 'error' });
      return;
    }
    if (!persForm.title.trim() && !persForm.body.trim()) {
      setSnackbar({ open: true, message: 'Заполни заголовок или текст', severity: 'error' });
      return;
    }
    setPersSending(true);
    try {
      await axios.post(
        `${SLOTS_API}/lobby-announcement/personal`,
        { userId: persUser.id, ...persForm },
        { headers: getAuthHeaders() }
      );
      setPersForm({ title: '', body: '', linkUrl: '', linkText: '' });
      setPersUser(null);
      setPersFound([]);
      setPersQuery('');
      loadPersonalNotices(0);
      setSnackbar({ open: true, message: 'Отправлено — увидит при заходе в лобби', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Ошибка: ' + (error.response?.data?.error || error.message), severity: 'error' });
    } finally {
      setPersSending(false);
    }
  };

  // 👤 Отзыв личного уведомления (пока игрок не нажал OK)
  const cancelPersonalNotice = async (id) => {
    try {
      await axios.delete(`${SLOTS_API}/lobby-announcement/personal/${id}`, { headers: getAuthHeaders() });
      loadPersonalNotices(persPage);
      setSnackbar({ open: true, message: 'Уведомление отозвано', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Ошибка: ' + (error.response?.data?.error || error.message), severity: 'error' });
    }
  };

  // Время рассылок/нажатий — всегда МСК
  const fmtAnnTime = (ts) => ts
    ? new Date(ts).toLocaleString('ru-RU', {
        timeZone: 'Europe/Moscow',
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit'
      })
    : '—';

  // 📢 Сохранение/публикация уведомления лобби
  const saveAnnouncement = async () => {
    if (annForm.enabled && !annForm.title.trim() && !annForm.body.trim()) {
      setSnackbar({ open: true, message: 'Заполни заголовок или текст', severity: 'error' });
      return;
    }
    setAnnSaving(true);
    try {
      const res = await axios.put(`${SLOTS_API}/lobby-announcement`, annForm, { headers: getAuthHeaders() });
      const a = res.data.announcement;
      setAnnMeta({ version: a.version, updatedAt: a.updated_at });
      loadAnnBroadcasts(0); // новая публикация сразу видна в истории
      setSnackbar({
        open: true,
        message: a.enabled
          ? `Опубликовано (v${a.version})${a.test_only ? ' — тест, видишь только ты' : ' — видят ВСЕ'}`
          : 'Сохранено, уведомление выключено',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({ open: true, message: 'Ошибка: ' + (error.response?.data?.error || error.message), severity: 'error' });
    } finally {
      setAnnSaving(false);
    }
  };

  // v25.01.24 — снесено 15.09.2026: loadTopPlayersToday тянул
  // /top-players-today (агрегат по slots_spins, ~90 мс, БЕЗ кэша) при старте,
  // каждые 60 с, на КАЖДЫЙ bigWin из SSE и по кнопке «Обновить» — а результат
  // никуда не рисовался, состояние topPlayersToday никто не читал. Вкладка
  // «Пользователи» живёт на /top-players-by-date, он и остался.

  // v25.01.24: Загрузка бонусных игр с пагинацией и фильтром по игре
  const loadRecentBonuses = async (page, game) => {
    try {
      const p = page || bonusPageRef.current;
      const g = game !== undefined ? game : bonusGameFilterRef.current;
      const gameParam = g ? `&game=${g}` : '';
      const res = await slotsGet.get(`${SLOTS_API}/recent-bonuses?page=${p}&limit=10${gameParam}`, { headers: getAuthHeaders() });
      setRecentBonuses(res.data.recentBonuses || []);
      if (res.data.totalPages) setBonusTotalPages(res.data.totalPages);
    } catch (error) {
      console.error('Ошибка загрузки бонусных игр:', error);
    }
  };

  // Раскрытие/закрытие деталей пользователя (expandable row)
  const toggleUserDetails = async (userId) => {
    // Если уже раскрыт - закрываем
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      setUserDetails(null);
      return;
    }

    // Раскрываем и загружаем данные
    setExpandedUserId(userId);
    try {
      const res = await slotsGet.get(`${SLOTS_API}/users/${userId}`, { headers: getAuthHeaders() });
      setUserDetails(res.data);
    } catch (error) {
      console.error('Ошибка загрузки деталей пользователя:', error);
      setSnackbar({ open: true, message: 'Ошибка загрузки деталей', severity: 'error' });
      setExpandedUserId(null);
    }
  };

  // Сброс статистики всех слотов
  const handleResetStats = async () => {
    if (!window.confirm('⚠️ Вы уверены что хотите сбросить ВСЮ статистику слотов?\n\nЭто действие НЕОБРАТИМО!')) {
      return;
    }

    setSaving(true);
    try {
      const res = await axios.delete(`${SLOTS_API}/stats/reset`, { headers: getAuthHeaders(), data: { adminId: 1 } });
      setSnackbar({
        open: true,
        message: `Статистика сброшена! Удалено спинов: ${res.data.deletedSpins}`,
        severity: 'success'
      });
      loadData(); // Перезагружаем данные
    } catch (error) {
      console.error('Ошибка сброса статистики:', error);
      setSnackbar({ open: true, message: 'Ошибка сброса статистики', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Сброс статистики игры БЕЗ лидерборда (сохраняет текущую неделю)
  const handleResetStatsKeepLeaderboard = async (gameId, gameName) => {
    if (!window.confirm(`⚠️ Сбросить статистику "${gameName}"?\n\nЛидерборд (текущая неделя) СОХРАНИТСЯ!\nСтатистика в админке обнулится.`)) {
      return;
    }

    setSaving(true);
    try {
      const res = await axios.delete(`${SLOTS_API}/stats/reset/${gameId}/keep-leaderboard`, { headers: getAuthHeaders() });
      setSnackbar({
        open: true,
        message: `Статистика "${gameName}" сброшена! Удалено: ${res.data.deletedSpins} спинов. Сохранено для лидерборда: ${res.data.keptSpins}`,
        severity: 'success'
      });
      loadData(); // Перезагружаем данные
    } catch (error) {
      console.error('Ошибка сброса статистики:', error);
      setSnackbar({ open: true, message: 'Ошибка сброса статистики', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Загрузка только статистики (для автообновления RTP)
  const loadStatsOnly = async () => {
    try {
      const res = await slotsGet.get(`${SLOTS_API}/stats`, { headers: getAuthHeaders() });
      setStats(res.data);
    } catch (error) {
      console.error('Ошибка обновления статистики:', error);
    }
  };

  // v25.01.14: Загрузка онлайн игроков (активных за последние 5 минут)
  const loadOnlinePlayers = async () => {
    try {
      const res = await slotsGet.get(`${SLOTS_API}/online`, { headers: getAuthHeaders() });
      setOnlinePlayers(res.data.onlinePlayers || 0);
    } catch (error) {
      console.error('Ошибка загрузки онлайн игроков:', error);
    }
  };

  // v26.02.07: Загрузка истории профита по дням + сводка
  const loadProfitHistory = async (page = 0) => {
    try {
      const res = await slotsGet.get(`${SLOTS_API}/profit-history?page=${page}&limit=10`, { headers: getAuthHeaders() });
      setProfitHistory(res.data.daily || []);
      setProfitHistoryTotalPages(res.data.totalPages || 1);
      if (res.data.summaries) setProfitSummaries(res.data.summaries);
    } catch (error) {
      console.error('Ошибка загрузки истории профита:', error);
    }
  };

  // v26.02.07: Загрузка топ игроков за конкретную дату
  const loadTopPlayersByDate = async (date = null) => {
    setTopPlayersLoading(true);
    setTopPlayersError(null);
    try {
      const url = date
        ? `${SLOTS_API}/top-players-by-date?date=${date}`
        : `${SLOTS_API}/top-players-by-date`;
      const res = await slotsGet.get(url, { headers: getAuthHeaders() });
      setTopPlayersByDate(res.data.topPlayers || []);
      setTopProfitPlayers(res.data.topProfitPlayers || []);
      if (res.data.dayTotal) setDayTotal(res.data.dayTotal);
      if (res.data.availableDays) setTopPlayersAvailableDays(res.data.availableDays);
    } catch (error) {
      console.error('Ошибка загрузки топ игроков по дате:', error);
      setTopPlayersError(
        error.code === 'ECONNABORTED'
          ? 'Сервер не ответил за 90 секунд. Нажми «Обновить».'
          : `Не удалось загрузить: ${error.response?.data?.error || error.message}`
      );
    } finally {
      setTopPlayersLoading(false);
    }
  };

  // v26.02.08: Загрузка gamble лидерборда
  const loadGambleLeaderboard = async () => {
    try {
      const res = await slotsGet.get(`${SLOTS_API}/gamble-leaderboard`, { headers: getAuthHeaders() });
      setGambleLeaderboard(res.data.leaderboard || []);
    } catch (error) {
      console.error('Ошибка загрузки gamble лидерборда:', error);
    }
  };

  useEffect(() => {
    // Первоначальная загрузка всех данных
    loadData();
    loadLiveData();
    loadJackpotWinners();
    loadRecentBonuses();
    loadOnlinePlayers();
    loadTopPlayersByDate();
    loadGambleLeaderboard();
    // ⚡ loadSlotUsers() и loadProfitHistory() убраны из старта — это тяжёлые
    // full-scan запросы, нужны только для вкладки «Аналитика». Грузим лениво
    // при её открытии (см. useEffect ниже), чтобы вкладка «Пользователи» и
    // первый рендер были быстрыми.

    // 🔴 SSE: Реактивные обновления без polling!
    // Подписываемся на события - данные обновляются ТОЛЬКО когда есть изменения
    let eventSource = null;
    // 🚀 Throttle для шапки LIVE (Онлайн/Спинов/Профит/RTP):
    // SSE statsData приходит каждые 10 сек, но юзер просил шапку
    // обновлять не чаще раз в 30 сек — чтобы цифры не моргали.
    let lastStatsHeaderUpdate = 0;
    const HEADER_THROTTLE_MS = 30000;
    try {
      // SSE (EventSource) не поддерживает заголовки — токен через query param
      const sseToken = localStorage.getItem('adminToken');
      eventSource = new EventSource(`${SLOTS_API}/events${sseToken ? `?token=${sseToken}` : ''}`);

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Реагируем на разные типы событий
          switch (data.type) {
            case 'statsData':
              // Шапка LIVE — обновляем не чаще раз в 30 сек.
              if (Date.now() - lastStatsHeaderUpdate >= HEADER_THROTTLE_MS) {
                if (data.data?.stats) {
                  setStats(data.data.stats);
                }
                if (data.data?.jackpotPool) {
                  setCurrentJackpotInfo(prev => prev ? { ...prev, ...data.data.jackpotPool } : data.data.jackpotPool);
                }
                lastStatsHeaderUpdate = Date.now();
              }
              break;
            case 'bonusTriggered':
            case 'bonusCompleted':
              loadRecentBonuses();
              loadLiveData();
              break;
            case 'bigWin':
              loadLiveData();
              break;
            case 'jackpot':
              loadJackpotWinners();
              loadLiveData();
              break;
            case 'statsUpdate':
              // Тоже под throttle — это касается шапки.
              if (Date.now() - lastStatsHeaderUpdate >= HEADER_THROTTLE_MS) {
                loadStatsOnly();
                lastStatsHeaderUpdate = Date.now();
              }
              break;
            case 'playerOnline':
              // Онлайн-счётчик в шапке — тоже не чаще раз в 30 сек.
              if (Date.now() - lastStatsHeaderUpdate >= HEADER_THROTTLE_MS) {
                loadOnlinePlayers();
                lastStatsHeaderUpdate = Date.now();
              }
              break;
            case 'connected':
              console.log('[SSE] Подключено к серверу');
              break;
            case 'heartbeat':
              break;
            default:
              if (data.type !== 'heartbeat') {
                loadLiveData();
              }
          }
        } catch (e) {
          console.error('[SSE] Ошибка парсинга:', e);
        }
      };

      eventSource.onerror = (error) => {
        console.error('[SSE] Ошибка соединения:', error);
        // При ошибке SSE - fallback на polling каждые 30 сек
      };
    } catch (e) {
      console.error('[SSE] Не удалось подключиться:', e);
    }

    // 🔴 LIVE вкладка — polling 5 сек (бэкенд кэширует 4 сек, ~80% запросов из кэша).
    // SSE дополнительно пушит мгновенно при бонусах/джекпотах/бигвинах.
    // v26.05.24: было 3 сек без кэша → 270% CPU; 30 сек → рывками; 5 сек + кэш — компромисс.
    const liveInterval = setInterval(() => {
      loadLiveData();
    }, 5000);

    // 🚀 Fallback polling (SSE broadcast = основной источник, polling = резерв)
    // Статистика: SSE пушит каждые 10 сек, polling 60 сек как fallback
    const statsInterval = setInterval(() => {
      loadStatsOnly();
      loadOnlinePlayers();
    }, 60000);

    // Бонусы: SSE пушит при bonusCompleted, polling 60 сек как fallback
    const bonusInterval = setInterval(() => {
      loadRecentBonuses();
    }, 60000);

    // Джекпоты: SSE пушит при jackpot + пул в statsData, polling 60 сек
    const jackpotInterval = setInterval(() => {
      loadJackpotWinners();
    }, 60000);

    return () => {
      if (eventSource) {
        eventSource.close();
      }
      clearInterval(liveInterval);
      clearInterval(statsInterval);
      clearInterval(bonusInterval);
      clearInterval(jackpotInterval);
    };
  }, []);

  // ⚡ Ленивая загрузка тяжёлой аналитики: только при первом открытии
  // вкладки «Аналитика» (index 3) — Все игроки + Профит по дням + Сводка.
  useEffect(() => {
    if (tabValue === 3 && !analyticsLoaded) {
      loadProfitHistory(0);
      loadSlotUsers();
      setAnalyticsLoaded(true);
    }
  }, [tabValue, analyticsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // 📢 Ленивая загрузка уведомления лобби при первом открытии вкладки.
  // ⚠️ Индекс — ANN_TAB_INDEX, а не число вручную: после добавления вкладки
  //    Sugar Strike номер уехал на 6, а здесь оставался 5 — вкладка вечно
  //    крутила спиннер, потому что загрузка не запускалась вообще.
  useEffect(() => {
    if (tabValue === ANN_TAB_INDEX && !annLoaded) {
      loadAnnouncement();
      loadAnnBroadcasts(0);
      loadPersonalNotices(0);
    }
  }, [tabValue, annLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Убирает лишние нули: 35.00 -> 35, 35.50 -> 35.5
  const formatNumber = (num) => {
    const n = parseFloat(num);
    if (isNaN(n)) return num;
    return String(n);
  };

  // Таблица топ-игроков за день (общая для «принесли прибыль» и «минуснули нас»).
  // Обе колонки P&L — глазами казино; строка красится по player_profit.
  const renderTopPlayersTable = (players, emptyText) => (
    players.length > 0 ? (
              <TableContainer component={Paper} sx={{ bgcolor: 'rgba(99, 102, 241, 0.1)' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Игрок</TableCell>
                      <TableCell align="right">Спинов</TableCell>
                      <TableCell align="right">Ставки</TableCell>
                      <TableCell align="right">Выигрыши</TableCell>
                      <TableCell align="right">Наш P&L (день)</TableCell>
                      <TableCell align="right">Наш P&L (всего)</TableCell>
                      <TableCell align="right">Макс. вин</TableCell>
                      <TableCell align="center" sx={{ px: 0.5, minWidth: 40 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {players.map((p, idx) => (
                      <React.Fragment key={p.id}>
                        <TableRow
                          sx={{
                            bgcolor: p.playerProfit > 0
                              ? 'rgba(244, 67, 54, 0.08)'
                              : 'rgba(76, 175, 80, 0.08)',
                            '& > *': { borderBottom: expandedUserId === p.id ? 'unset' : undefined }
                          }}
                        >
                          <TableCell>
                            <Chip
                              label={idx + 1}
                              size="small"
                              sx={{
                                bgcolor: idx === 0 ? '#ffd700' : idx === 1 ? '#c0c0c0' : idx === 2 ? '#cd7f32' : '#555',
                                color: idx < 3 ? '#000' : '#fff',
                                fontWeight: 'bold',
                                minWidth: 28
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography
                              component="span"
                              sx={{ fontWeight: 'bold', cursor: 'pointer', '&:hover': { color: '#6366f1', textDecoration: 'underline' } }}
                              onClick={() => openGambleMenu(p.id)}
                            >
                              {p.displayName}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{p.spinsCount}</TableCell>
                          <TableCell align="right">{p.totalBets.toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ color: '#4caf50' }}>
                            {p.totalWins.toFixed(2)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 'bold',
                              color: p.playerProfit > 0 ? '#f44336' : '#4caf50'
                            }}
                            title="Касса за сегодня (перспектива казино). + = мы заработали сегодня, − = слили игроку"
                          >
                            {(-p.playerProfit) > 0 ? '+' : ''}{(-p.playerProfit).toFixed(2)}
                            {(-p.playerProfit) >= 0 ? ' 📈' : ' 📉'}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 'bold',
                              color: (p.realPnlCasino ?? 0) > 0 ? '#4caf50' : (p.realPnlCasino ?? 0) < 0 ? '#f44336' : 'inherit'
                            }}
                            title="Занёс − Вывел − что ещё может забрать (баланс, копилка, заявки, звёздный стейк), за всё время. + казино в плюсе, − слили юзеру"
                          >
                            {(p.realPnlCasino ?? 0) > 0 ? '+' : ''}{(p.realPnlCasino ?? 0).toFixed(2)}
                          </TableCell>
                          <TableCell align="right">{p.maxWin.toFixed(2)}</TableCell>
                          <TableCell align="center" sx={{ px: 0.5, minWidth: 40 }}>
                            <IconButton
                              size="small"
                              sx={{ width: 28, height: 28, flexShrink: 0, color: '#fbbf24', border: '1px solid rgba(251,191,36,0.45)', '&:hover': { bgcolor: 'rgba(251,191,36,0.12)' }, '& svg': { maxWidth: 'none', width: 20, height: 20 } }}
                              onClick={() => toggleUserDetails(p.id)}
                              title={expandedUserId === p.id ? 'Скрыть детали' : 'Показать детали'}
                            >
                              {expandedUserId === p.id ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        {/* Раскрывающиеся детали */}
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                            <Collapse in={expandedUserId === p.id} timeout="auto" unmountOnExit>
                              {expandedUserId === p.id && userDetails && (
                                <Box sx={{ py: 2, px: 1 }}>
                                  <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid item xs={4}>
                                      <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'rgba(30, 41, 59, 0.5)' }}>
                                        <Typography variant="caption" color="text.secondary">Telegram ID</Typography>
                                        <Typography variant="body2" fontWeight="bold">{userDetails.user.telegramId}</Typography>
                                      </Paper>
                                    </Grid>
                                    <Grid item xs={4}>
                                      <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'rgba(30, 41, 59, 0.5)' }}>
                                        <Typography variant="caption" color="text.secondary">Баланс</Typography>
                                        <Typography variant="body2" fontWeight="bold">{userDetails.user.balance.toFixed(2)} SPERM</Typography>
                                      </Paper>
                                    </Grid>
                                    <Grid item xs={4}>
                                      <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'rgba(30, 41, 59, 0.5)' }}>
                                        <Typography variant="caption" color="text.secondary">Всего спинов</Typography>
                                        <Typography variant="body2" fontWeight="bold">{userDetails.gameStats.reduce((s, g) => s + g.spins, 0)}</Typography>
                                      </Paper>
                                    </Grid>
                                  </Grid>

                                  {/* 🎯 Объективный P&L (касса) — главное число */}
                                  <CashTruthBlock user={userDetails.user} />

                                  {/* Откуда профит: разбивка по слотам и источникам */}
                                  {userDetails.gameStats.length > 0 && <ProfitBreakdown gameStats={userDetails.gameStats} />}

                                  {/* Лояльность сегодня: разбивка по источникам */}
                                  {userDetails.loyaltyToday && (
                                    <Box sx={{ mb: 2 }}>
                                      <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block', color: '#9c27b0' }}>
                                        💎 Лояльность сегодня
                                      </Typography>
                                      <Paper sx={{ p: 1.5, bgcolor: 'rgba(156, 39, 176, 0.08)', mb: 1 }}>
                                        <Grid container spacing={1}>
                                          <Grid item xs={4}>
                                            <Typography variant="caption" color="text.secondary">Профит со слотов</Typography>
                                            <Typography variant="body2" fontWeight="bold" sx={{ color: userDetails.loyaltyToday.slotProfit >= 0 ? '#4caf50' : '#f44336' }}>
                                              {userDetails.loyaltyToday.slotProfit >= 0 ? '+' : ''}{userDetails.loyaltyToday.slotProfit.toFixed(2)} SPERM
                                            </Typography>
                                          </Grid>
                                          <Grid item xs={4}>
                                            <Typography variant="caption" color="text.secondary">Wager отыграно (live)</Typography>
                                            <Typography variant="body2" fontWeight="bold" sx={{ color: '#4caf50' }}>
                                              +{userDetails.loyaltyToday.wagerUnlockedTotal.toFixed(2)} SPERM
                                            </Typography>
                                          </Grid>
                                          <Grid item xs={4}>
                                            <Typography variant="caption" color="text.secondary">Итого live-профит</Typography>
                                            <Typography variant="body2" fontWeight="bold" sx={{ color: userDetails.loyaltyToday.livePnLTotal >= 0 ? '#4caf50' : '#f44336' }}>
                                              {userDetails.loyaltyToday.livePnLTotal >= 0 ? '+' : ''}{userDetails.loyaltyToday.livePnLTotal.toFixed(2)} SPERM
                                            </Typography>
                                          </Grid>
                                        </Grid>
                                      </Paper>

                                      {userDetails.loyaltyToday.wagerUnlocks.length > 0 && (
                                        <Box sx={{ mb: 1 }}>
                                          <Typography variant="caption" sx={{ color: '#4caf50' }}>
                                            ✅ Разблокировано отыгрышем (зачислено в баланс):
                                          </Typography>
                                          <Table size="small" sx={{ bgcolor: 'rgba(76, 175, 80, 0.05)' }}>
                                            <TableBody>
                                              {userDetails.loyaltyToday.wagerUnlocks.map((u) => (
                                                <TableRow key={`unlock-${u.source}`}>
                                                  <TableCell sx={{ py: 0.3 }}>{u.source}</TableCell>
                                                  <TableCell align="right" sx={{ py: 0.3 }}>× {u.count}</TableCell>
                                                  <TableCell align="right" sx={{ py: 0.3, color: '#4caf50', fontWeight: 'bold' }}>+{u.amount.toFixed(2)} SPERM</TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </Box>
                                      )}

                                      {userDetails.loyaltyToday.pendingCredits.length > 0 && (
                                        <Box>
                                          <Typography variant="caption" sx={{ color: '#ff9800' }}>
                                            ⏳ В MoonStash (под отыгрышем, пока недоступно):
                                          </Typography>
                                          <Table size="small" sx={{ bgcolor: 'rgba(255, 152, 0, 0.05)' }}>
                                            <TableBody>
                                              {userDetails.loyaltyToday.pendingCredits.map((c) => (
                                                <TableRow key={`pending-${c.source}`}>
                                                  <TableCell sx={{ py: 0.3 }}>{c.source}</TableCell>
                                                  <TableCell align="right" sx={{ py: 0.3 }}>× {c.count}</TableCell>
                                                  <TableCell align="right" sx={{ py: 0.3, color: '#ff9800' }}>+{c.amount.toFixed(2)} SPERM</TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </Box>
                                      )}
                                    </Box>
                                  )}

                                  {/* Топ 10 заносов сегодня */}
                                  {userDetails.todayBigWins && userDetails.todayBigWins.length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                      <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block', color: '#ffc107' }}>
                                        Топ 10 заносов сегодня (по иксам):
                                      </Typography>
                                      <Table size="small" sx={{ bgcolor: 'rgba(255, 193, 7, 0.1)' }}>
                                        <TableHead>
                                          <TableRow>
                                            <TableCell sx={{ py: 0.5 }}>#</TableCell>
                                            <TableCell sx={{ py: 0.5 }}>Игра</TableCell>
                                            <TableCell align="right" sx={{ py: 0.5 }}>Ставка</TableCell>
                                            <TableCell align="right" sx={{ py: 0.5 }}>Выигрыш</TableCell>
                                            <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold' }}>x</TableCell>
                                            <TableCell sx={{ py: 0.5 }}>Время</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {userDetails.todayBigWins.map((bw, idx) => (
                                            <TableRow key={bw.id}>
                                              <TableCell sx={{ py: 0.5, fontWeight: 'bold', color: idx < 3 ? '#ffc107' : 'inherit' }}>{idx + 1}</TableCell>
                                              <TableCell sx={{ py: 0.5 }}>{bw.gameName}</TableCell>
                                              <TableCell align="right" sx={{ py: 0.5 }}>{bw.bet.toFixed(2)}</TableCell>
                                              <TableCell align="right" sx={{ py: 0.5, color: '#4caf50', fontWeight: 'bold' }}>{bw.win.toFixed(2)}</TableCell>
                                              <TableCell align="right" sx={{ py: 0.5, color: '#ffc107', fontWeight: 'bold' }}>x{bw.multiplier}</TableCell>
                                              <TableCell sx={{ py: 0.5 }}>{new Date(bw.date).toLocaleTimeString('ru-RU')}</TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </Box>
                                  )}

                                  {/* Большие выигрыши */}
                                  {userDetails.bigWins.length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                      <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block', color: '#4caf50' }}>
                                        <TrendingUp sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                                        Большие выигрыши (x10+):
                                      </Typography>
                                      <Table size="small" sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)' }}>
                                        <TableHead>
                                          <TableRow>
                                            <TableCell sx={{ py: 0.5 }}>Игра</TableCell>
                                            <TableCell align="right" sx={{ py: 0.5 }}>Ставка</TableCell>
                                            <TableCell align="right" sx={{ py: 0.5 }}>Выигрыш</TableCell>
                                            <TableCell align="right" sx={{ py: 0.5 }}>x</TableCell>
                                            <TableCell sx={{ py: 0.5 }}>Дата</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {userDetails.bigWins.slice(0, 5).map((bw) => (
                                            <TableRow key={bw.id}>
                                              <TableCell sx={{ py: 0.5 }}>{bw.gameName}</TableCell>
                                              <TableCell align="right" sx={{ py: 0.5 }}>{bw.bet.toFixed(2)}</TableCell>
                                              <TableCell align="right" sx={{ py: 0.5, color: '#4caf50', fontWeight: 'bold' }}>{bw.win.toFixed(2)}</TableCell>
                                              <TableCell align="right" sx={{ py: 0.5 }}>x{bw.multiplier}</TableCell>
                                              <TableCell sx={{ py: 0.5 }}>{new Date(bw.date).toLocaleString('ru-RU')}</TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </Box>
                                  )}

                                  {/* Последние спины */}
                                  <Box>
                                    <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>Последние 10 спинов:</Typography>
                                    <Table size="small" sx={{ bgcolor: 'rgba(30, 41, 59, 0.3)' }}>
                                      <TableHead>
                                        <TableRow>
                                          <TableCell sx={{ py: 0.5 }}>Игра</TableCell>
                                          <TableCell align="right" sx={{ py: 0.5 }}>Ставка</TableCell>
                                          <TableCell align="right" sx={{ py: 0.5 }}>Выигрыш</TableCell>
                                          <TableCell sx={{ py: 0.5 }}>Дата</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {userDetails.recentSpins.slice(0, 10).map((spin) => (
                                          <TableRow key={spin.id}>
                                            <TableCell sx={{ py: 0.5 }}>{spin.gameName}</TableCell>
                                            <TableCell align="right" sx={{ py: 0.5 }}>{spin.bet.toFixed(2)}</TableCell>
                                            <TableCell align="right" sx={{ py: 0.5, color: spin.win > 0 ? '#4caf50' : 'inherit' }}>{spin.win.toFixed(2)}</TableCell>
                                            <TableCell sx={{ py: 0.5 }}>{new Date(spin.date).toLocaleString('ru-RU')}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                </Box>
                              )}
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
    ) : topPlayersLoading ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">Считаем игроков за день…</Typography>
      </Box>
    ) : topPlayersError ? (
      <Alert severity="error">{topPlayersError}</Alert>
    ) : (
      <Alert severity="info">{emptyText}</Alert>
    )
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Casino /> Настройки слотов
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadData}
        >
          Обновить
        </Button>
      </Box>

      <Paper sx={{ bgcolor: 'rgba(30, 41, 59, 0.8)', p: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<BarChart />} label="Статистика" />
          <Tab icon={<PlayCircle />} label="Live" />
          <Tab icon={<People />} label="Пользователи" />
          <Tab icon={<Insights />} label="Аналитика" />
          <Tab icon={<FlightTakeoff />} label="Авиатор" />
          <Tab icon={<GpsFixed />} label="Sugar Strike" />
          <Tab icon={<BubbleChart />} label="Плинко" />
          <Tab icon={<RocketLaunch />} label="Лесенка" />
          <Tab icon={<SportsEsports />} label="Wiggle Wars" />
          <Tab icon={<Campaign />} label="Уведомление" />
        </Tabs>

        {/* ТАБ 1: Live */}
        <TabPanel value={tabValue} index={1}>
          {/* Заголовок и RTP справа */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6">Последние спины</Typography>
              <Chip icon={<PlayCircle />} label="5 сек" color="success" variant="outlined" size="small" />
            </Box>
            <Typography variant="body2" sx={{ textAlign: 'right' }}>
              <span style={{ color: onlinePlayers > 0 ? '#4caf50' : '#888' }}>🟢 Онлайн:</span>{' '}
              <span style={{ color: onlinePlayers > 0 ? '#4caf50' : '#666', fontWeight: 'bold' }}>{onlinePlayers}</span> | {' '}
              <span style={{ color: '#888' }}>Профит:</span>{' '}
              {(() => {
                const realProfit = stats?.general?.real_profit !== undefined
                  ? stats.general.real_profit
                  : ((stats?.general?.total_bets || 0) - (stats?.general?.total_wins || 0) - (stats?.general?.loyalty_expense || 0));
                return (
                  <span style={{
                    color: realProfit > 0 ? '#4caf50' : '#f44336',
                    fontWeight: 'bold'
                  }}>
                    {realProfit > 0 ? '+' : ''}{Number(realProfit).toFixed(0)}
                  </span>
                );
              })()} | {' '}
              <span style={{ color: '#888' }}>RTP:</span>{' '}
              {(() => {
                // RTP с лоялти и джекпотом (джекпот уже в total_wins, лоялти добавляем)
                const rtpFull = stats?.general?.total_bets > 0
                  ? (stats.general.total_wins + (stats.general.loyalty_expense || 0)) / stats.general.total_bets * 100
                  : 0;
                return (
                  <span style={{
                    fontWeight: 'bold',
                    color: stats?.general?.total_bets > 0
                      ? rtpFull > 95 ? '#f44336' : rtpFull > 85 ? '#ff9800' : '#4caf50'
                      : '#666'
                  }}>
                    {Math.round(rtpFull)}%
                  </span>
                );
              })()}
            </Typography>
          </Box>

          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Время</TableCell>
                  <TableCell>Игрок</TableCell>
                  <TableCell>Игра</TableCell>
                  <TableCell align="right">Ставка</TableCell>
                  <TableCell align="right">Выигрыш</TableCell>
                  <TableCell align="center">Статус</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {liveSpins.map((spin) => (
                  <TableRow
                    key={`${spin.event_type || 'spin'}-${spin.id}`}
                    sx={{
                      bgcolor: spin.event_type === 'sugar'
                        ? (spin.ss_net10s > 0
                            ? 'rgba(16, 185, 129, 0.10)'
                            : spin.ss_net10s < 0
                            ? 'rgba(244, 67, 54, 0.10)'
                            : 'transparent')
                        : spin.spinType === '🃏 КАРТА WIN'
                        ? 'rgba(76, 175, 80, 0.15)'
                        : spin.spinType === '🃏 КАРТА LOSE'
                        ? 'rgba(244, 67, 54, 0.15)'
                        : spin.is_jackpot
                        ? 'rgba(255, 215, 0, 0.2)'
                        : spin.win_amount > spin.bet_amount * 5
                        ? 'rgba(16, 185, 129, 0.15)'
                        : spin.win_amount > 0
                        ? 'rgba(16, 185, 129, 0.05)'
                        : 'transparent'
                    }}
                  >
                    <TableCell>
                      {new Date(spin.created_at).toLocaleTimeString('ru-RU')}
                    </TableCell>
                    <TableCell>
                      {spin.username || `ID: ${spin.telegram_id}`}
                      {spin.isPromoFree && (
                        <Box component="span" title="Промо-фриспин (по промокоду)"
                          sx={{ ml: 0.5, px: 0.5, borderRadius: 0.5, fontSize: 10, fontWeight: 800,
                                color: '#22e0c8', border: '1px solid #22e0c888', bgcolor: '#22e0c822' }}>F</Box>
                      )}
                      {spin.isWagerSpin && (
                        <Box component="span" title="Вагер-спин: оплачен из локнутого кэшбэка (вне GGR/джекпота)"
                          sx={{ ml: 0.5, px: 0.5, borderRadius: 0.5, fontSize: 10, fontWeight: 800,
                                color: '#f5b301', border: '1px solid #f5b30188', bgcolor: '#f5b30122' }}>V</Box>
                      )}
                    </TableCell>
                    <TableCell>{spin.game_name}</TableCell>
                    {/* §24.07 (владелец): у SS в колонке стоит РЕАЛЬНАЯ цена выстрела
                        (ставка·costMult), базовая ставка — подписью под ней. */}
                    <TableCell align="right">
                      {spin.event_type === 'sugar' ? (
                        <Box>
                          <Box component="span" sx={{ fontWeight: 'bold' }}>{ssMoney(spin.bet_amount)}</Box>
                          {spin.ss_cost_mult > 1 && (
                            <Typography variant="caption" sx={{ display: 'block', color: '#888' }}
                              title="базовая ставка × надбавка за мощность пушки">
                              база {ssMoney(spin.ss_stake)} ×{Number(spin.ss_cost_mult).toFixed(2)}
                            </Typography>
                          )}
                        </Box>
                      ) : parseFloat(spin.bet_amount).toFixed(2)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: spin.win_amount > 0 ? '#10b981' : 'inherit',
                        fontWeight: spin.win_amount > spin.bet_amount * 3 ? 'bold' : 'normal'
                      }}
                    >
                      {spin.event_type === 'sugar' ? (
                        <Box>
                          <Box component="span" sx={{
                            color: spin.ss_net10s > 0 ? '#10b981' : spin.ss_net10s < 0 ? '#f44336' : '#888',
                            fontWeight: 'bold'
                          }}>
                            {spin.ss_net10s > 0 ? '+' : ''}{Number(spin.ss_net10s).toFixed(2)}
                          </Box>
                          <Typography variant="caption" sx={{ display: 'block', color: '#888' }}>за 10с</Typography>
                        </Box>
                      ) : (
                        parseFloat(spin.win_amount).toFixed(2)
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {/* §LIVE-ФИД: Sugar Strike — плашка SS/ДЕМО + темп выстрелов/сек */}
                      {spin.event_type === 'sugar' && (
                        <Box sx={{ display: 'flex', gap: 0.3, justifyContent: 'center', flexWrap: 'wrap' }}>
                          <Chip
                            label={spin.ss_demo ? '🎮 ДЕМО' : '🍬 SS'}
                            size="small"
                            sx={{ bgcolor: spin.ss_demo ? '#607d8b' : '#22c1a6', color: 'white', fontWeight: 'bold', fontSize: '0.6rem', height: '18px', '& .MuiChip-label': { px: 0.6 } }}
                          />
                          <Chip
                            label={`⚡ ${Number(spin.ss_rate).toFixed(1)}/с`}
                            title="выстрелов в секунду"
                            size="small"
                            sx={{ bgcolor: '#37474f', color: '#b0bec5', fontSize: '0.6rem', height: '18px', '& .MuiChip-label': { px: 0.6 } }}
                          />
                        </Box>
                      )}
                      {/* v1.30: Gamble события + компактные плашки (-20%) */}
                      {spin.spinType === '🃏 КАРТА WIN' && (
                        <Chip
                          label={spin.gameId === 9 ? '🎴 ОМИКУДЗИ WIN' : '🃏 WIN'}
                          size="small"
                          sx={{
                            bgcolor: '#4caf50',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.65rem',
                            height: '20px',
                            '& .MuiChip-label': { px: 0.8 }
                          }}
                        />
                      )}
                      {spin.spinType === '🃏 КАРТА LOSE' && (
                        <Chip
                          label={spin.gameId === 9 ? '🎴 ОМИКУДЗИ LOSE' : '🃏 LOSE'}
                          size="small"
                          sx={{
                            bgcolor: '#f44336',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.65rem',
                            height: '20px',
                            '& .MuiChip-label': { px: 0.8 }
                          }}
                        />
                      )}
                      {spin.spinType === '🎰 BONUS START' && (
                        <Chip label="🎰 BONUS" color="secondary" size="small" sx={{ fontSize: '0.65rem', height: '20px', '& .MuiChip-label': { px: 0.8 } }} />
                      )}
                      {/* v17.3: Buy Bonus - отдельная плашка */}
                      {spin.isBuyBonus && (
                        <Chip label="💰 BUY BONUS" sx={{ bgcolor: '#e91e63', color: 'white', fontWeight: 'bold', fontSize: '0.65rem', height: '20px', '& .MuiChip-label': { px: 0.8 } }} size="small" />
                      )}
                      {/* v17.3: Обычный фриспин (без ретриггера) */}
                      {spin.is_free_spin && !spin.is_bonus_triggered && (
                        <Chip label="🔥 FREE" sx={{ bgcolor: '#ff6b35', color: 'white', fontSize: '0.65rem', height: '20px', '& .MuiChip-label': { px: 0.8 } }} size="small" />
                      )}
                      {/* v17.3: Ретриггер = фриспин + бонус триггер (4+ скаттеров в бонусе) */}
                      {spin.is_free_spin && spin.is_bonus_triggered && (
                        <Chip label="🔄 RETRIGGER" sx={{ bgcolor: '#9c27b0', color: 'white', fontWeight: 'bold', fontSize: '0.65rem', height: '20px', '& .MuiChip-label': { px: 0.8 } }} size="small" />
                      )}
                      {spin.spinType === '💎 JACKPOT' && (
                        <Chip label="💎 JACKPOT" color="warning" size="small" sx={{ fontSize: '0.65rem', height: '20px', '& .MuiChip-label': { px: 0.8 } }} />
                      )}
                      {spin.event_type !== 'gamble' && spin.bonus_spins_won > 0 && (
                        <Chip label={`+${spin.bonus_spins_won} FS`} color="info" size="small" sx={{ ml: 0.3, fontSize: '0.65rem', height: '20px', '& .MuiChip-label': { px: 0.8 } }} />
                      )}
                      {/* v15.9: Различаем GARGANTOON и BIG WIN по spinType! */}
                      {spin.is_bonus_triggered && spin.spinType && spin.spinType.includes('GARGANTOON') && (
                        <Chip
                          label="🦖 GARGANTOON"
                          size="small"
                          sx={{
                            bgcolor: '#9c27b0',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.65rem',
                            height: '20px',
                            '& .MuiChip-label': { px: 0.8 }
                          }}
                        />
                      )}
                      {spin.is_bonus_triggered && spin.spinType && spin.spinType.includes('BIG WIN') && (
                        <Chip
                          label="💰 BIG WIN"
                          size="small"
                          sx={{
                            bgcolor: '#4caf50',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.65rem',
                            height: '20px',
                            '& .MuiChip-label': { px: 0.8 }
                          }}
                        />
                      )}
                      {/* v17.3: BONUS триггер (обычный спин который запустил бонус) - НЕ показывать для buy bonus */}
                      {spin.is_bonus_triggered && !spin.is_free_spin && !spin.isBuyBonus && (!spin.spinType || (!spin.spinType.includes('GARGANTOON') && !spin.spinType.includes('BIG WIN'))) && (
                        <Chip label="🎰 BONUS" color="secondary" size="small" sx={{ fontSize: '0.65rem', height: '20px', '& .MuiChip-label': { px: 0.8 } }} />
                      )}
                      {/* v17.3: Ante Bet - дополнительный индикатор (ставка с удвоенным шансом) */}
                      {spin.isAnteBet && !spin.is_free_spin && (
                        <Chip
                          label="🎯 ANTE"
                          size="small"
                          sx={{
                            bgcolor: '#ff9800',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.6rem',
                            height: '18px',
                            ml: 0.3,
                            '& .MuiChip-label': { px: 0.6 }
                          }}
                        />
                      )}
                      {/* v25.01: TON Rangers бонусы - 🚂T, ⚔️D, 💀DD */}
                      {spin.gameId === 4 && spin.bonusType === 'train_heist' && (
                        <Chip
                          label="🚂 T"
                          size="small"
                          sx={{
                            bgcolor: '#f59e0b',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.65rem',
                            height: '20px',
                            ml: 0.3,
                            '& .MuiChip-label': { px: 0.6 }
                          }}
                        />
                      )}
                      {spin.gameId === 4 && spin.bonusType === 'star_duel' && (
                        <Chip
                          label="⚔️ D"
                          size="small"
                          sx={{
                            bgcolor: '#8b5cf6',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.65rem',
                            height: '20px',
                            ml: 0.3,
                            '& .MuiChip-label': { px: 0.6 }
                          }}
                        />
                      )}
                      {spin.gameId === 4 && spin.bonusType === 'dead_star' && (
                        <Chip
                          label="💀 DD"
                          size="small"
                          sx={{
                            bgcolor: '#ef4444',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.65rem',
                            height: '20px',
                            ml: 0.3,
                            '& .MuiChip-label': { px: 0.6 }
                          }}
                        />
                      )}
                      {/* v25.01: TON Rangers ретриггер ТОЛЬКО для Star Duel (D) */}
                      {spin.gameId === 4 && spin.bonusType === 'star_duel' && spin.isRetrigger && (
                        <Chip
                          label="🔄 R"
                          size="small"
                          sx={{
                            bgcolor: '#9c27b0',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.6rem',
                            height: '18px',
                            ml: 0.3,
                            '& .MuiChip-label': { px: 0.5 }
                          }}
                        />
                      )}
                      {/* 🌙 Kaguya Ways: какой бонус и путь выбран, апгрейд, N/всего, итог бонуса, мини-фича, Благословение */}
                      {spin.gameId === 9 && spin.event_type === 'spin' && (() => {
                        const g = spin.grid_result && typeof spin.grid_result === 'object' ? spin.grid_result : {};
                        const chip = (label, bg, title) => (
                          <Chip key={label} label={label} title={title} size="small"
                            sx={{ bgcolor: bg, color: 'white', fontWeight: 'bold', fontSize: '0.6rem', height: '18px', ml: 0.3, '& .MuiChip-label': { px: 0.6 } }} />
                        );
                        const out = [];
                        if (g.kg === 'fs') {
                          if (g.i === 1) out.push(g.bonus === 'full_moon' ? chip('🌕 ПОЛНОЛУНИЕ', '#5c6bc0', '4–5 скаттеров') : chip(KG_PATH[g.path] || '🌸 САКУРА', '#d81b60', 'путь «Выбора принцессы»'));
                          if (g.up) out.push(chip('🌕 АПГРЕЙД', '#5c6bc0', '«Ночь сакуры» → «Полнолуние»'));
                          if (g.n) out.push(chip(`${g.i}/${g.n}`, '#455a64', 'фриспин / всего в бонусе'));
                          if (g.max) out.push(chip('👑 MAX ×600', '#f5b301', 'потолок бонуса'));
                          else if (g.total_x != null) out.push(chip(`ИТОГ ×${g.total_x}`, '#00897b', 'выигрыш бонуса целиком'));
                        } else {
                          if (g.bonus === 'sakura') out.push(chip('🌸 САКУРА', '#d81b60', '3 скаттера → «Выбор принцессы»'));
                          if (g.bonus === 'full_moon') out.push(chip('🌕 ПОЛНОЛУНИЕ', '#5c6bc0', '4–5 скаттеров'));
                          if (g.blessing === true) out.push(chip('✨ БЛАГОСЛОВЕНИЕ', '#8e24aa', 'Благословение Кагуи сработало'));
                          if (g.mini && KG_MINI[g.mini]) out.push(chip(KG_MINI[g.mini], '#6d4c41', 'мини-фича героини'));
                        }
                        return out;
                      })()}
                      {spin.win_amount > spin.bet_amount * 10 && spin.spinType === 'normal' && !spin.is_bonus_triggered && (
                        <Chip label="BIG WIN" color="success" size="small" sx={{ fontSize: '0.65rem', height: '20px', '& .MuiChip-label': { px: 0.8 } }} />
                      )}
                      {spin.win_amount === 0 && spin.spinType === 'normal' && !spin.is_bonus_triggered && !spin.isAnteBet && (
                        <Typography variant="caption" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {liveSpins.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Пока нет данных о спинах
            </Alert>
          )}
        </TabPanel>


        {/* ТАБ 1: Статистика */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6">
                Статистика слотов
              </Typography>
              {/* v25.01.14: Счётчик онлайн игроков */}
              <Chip
                icon={<span style={{ fontSize: '10px' }}>🟢</span>}
                label={`Онлайн: ${onlinePlayers}`}
                color={onlinePlayers > 0 ? 'success' : 'default'}
                variant="outlined"
                size="small"
              />
            </Box>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteForever />}
              onClick={handleResetStats}
              disabled={saving}
            >
              Сбросить статистику
            </Button>
          </Box>

          {stats && (
            <>
              <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={6} sm={4} md={2}>
                  <Paper sx={{ p: 2, bgcolor: 'rgba(245, 158, 11, 0.2)', textAlign: 'center' }}>
                    <Typography variant="h5">
                      {Number(stats.general?.real_cash_profit || 0).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Профит казино</Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: '#888', fontSize: '0.65rem' }}>
                      реальная касса = занесено − выплачено
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Paper sx={{ p: 2, bgcolor: 'rgba(99, 102, 241, 0.2)', textAlign: 'center' }}>
                    <Typography variant="h5">{stats.general?.total_spins?.toLocaleString() || 0}</Typography>
                    <Typography variant="body2" color="text.secondary">Всего спинов</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Paper sx={{ p: 2, bgcolor: 'rgba(168, 85, 247, 0.2)', textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ color: '#a855f7' }}>
                      −{Number(stats.general?.loyalty_expense || 0).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Лояльность</Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: '#888', fontSize: '0.65rem' }}>
                      Сундуки: {Number(stats.general?.loyalty_chest_paid || 0).toLocaleString()} ·{' '}
                      MoonStash отыгр.: {Number(stats.general?.loyalty_moonstash_unlocked || 0).toLocaleString()}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Paper sx={{ p: 2, bgcolor: 'rgba(6, 182, 212, 0.2)', textAlign: 'center' }}>
                    <Typography variant="h5">
                      {stats.general?.total_bets > 0
                        ? Math.round(((stats.general?.total_wins + (stats.general?.loyalty_expense || 0)) / stats.general?.total_bets) * 100)
                        : 0}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Средний RTP</Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: '#888', fontSize: '0.65rem' }}>
                      с лоялти и джекпотом · игровой {stats.general?.total_bets > 0 ? Math.round((stats.general?.total_wins / stats.general?.total_bets) * 100) : 0}%
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Paper sx={{ p: 2, bgcolor: 'rgba(120, 113, 108, 0.18)', textAlign: 'center' }}>
                    <Typography variant="h5">
                      {Number(
                        stats.general?.real_profit !== undefined
                          ? stats.general.real_profit
                          : ((stats.general?.total_bets || 0) - (stats.general?.total_wins || 0) - (stats.general?.loyalty_expense || 0))
                      ).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Игровой профит (валовый)</Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: '#888', fontSize: '0.65rem' }}>
                      = ставки − выплаты − лояльность
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Paper sx={{ p: 2, bgcolor: 'rgba(236, 72, 153, 0.18)', textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ color: '#ec4899' }}>
                      {Number(stats.general?.player_liability || 0).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Балансы юзеров</Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: '#888', fontSize: '0.65rem' }}>
                      сейчас на балансах (инфо, не вычитается из профита)
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* v1.16: Статистика бонусных раундов */}
              <Typography variant="subtitle1" sx={{ mt: 3, mb: 2 }}>
                🔥 Бонусные раунды (Free Spins)
              </Typography>
              <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={6} sm={4} md={3}>
                  <Paper sx={{ p: 2, bgcolor: 'rgba(255, 107, 53, 0.2)', textAlign: 'center' }}>
                    <Typography variant="h5">
                      {stats.general?.bonuses_natural || 0}
                      {(stats.general?.retriggers || 0) > 0 && (
                        <span style={{ color: '#e91e63', fontSize: '0.6em' }}> (R{stats.general?.retriggers})</span>
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Бонусов (без R)</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Paper sx={{ p: 2, bgcolor: 'rgba(255, 107, 53, 0.2)', textAlign: 'center' }}>
                    <Typography variant="h5">{stats.general?.free_spins_played || 0}</Typography>
                    <Typography variant="body2" color="text.secondary">Фриспинов сыграно</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Paper sx={{ p: 2, bgcolor: 'rgba(255, 107, 53, 0.2)', textAlign: 'center' }}>
                    <Typography variant="h5">{Number(stats.general?.free_spins_wins || 0).toLocaleString()}</Typography>
                    <Typography variant="body2" color="text.secondary">Выиграно с FS</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Paper sx={{ p: 2, bgcolor: 'rgba(255, 107, 53, 0.2)', textAlign: 'center' }}>
                    <Typography variant="h5">{Number(stats.general?.max_free_spin_win || 0).toFixed(2)}</Typography>
                    <Typography variant="body2" color="text.secondary">Макс. выигрыш FS</Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Typography variant="h6" gutterBottom>
                RTP по играм
              </Typography>
              <TableContainer component={Paper} sx={{ bgcolor: 'rgba(30, 41, 59, 0.5)' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Игра</TableCell>
                      <TableCell align="right">Спинов</TableCell>
                      <TableCell align="right">Бонусов</TableCell>
                      <TableCell align="right">Выплаты</TableCell>
                      <TableCell align="right">🃏 Gamble</TableCell>
                      <TableCell align="right">RTP факт</TableCell>
                      <TableCell align="center">Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* moon_hunter (id=8) показываем МЕЖДУ Moon Dogs (id=6) и Авиатором (id=7).
                        Бэк отдаёт ORDER BY g.id → id=8 был бы в конце, поэтому переупорядочиваем в UI. */}
                    {[...(stats.byGame || [])]
                      .sort((a, b) => {
                        const order = (g) => (g.id === 8 ? 6.5 : g.id === 9 ? 6.6 : g.id); // 9 = Kaguya Ways после Moon Hunter
                        return order(a) - order(b);
                      })
                      .map((game) => (
                      <TableRow key={game.id}>
                        <TableCell>{game.display_name}</TableCell>
                        <TableCell align="right">
                          <div style={{ lineHeight: 1.4 }}>
                            <div>{Number(game.spins).toLocaleString()}</div>
                            {/* полный счёт (владелец 01.10): свои деньги + 🎁 подарочные + V на кэшбэк MoonStash */}
                            {Number(game.spins_all || 0) > Number(game.spins || 0) && (
                              <div style={{ fontSize: '0.72em', color: '#94a3b8', whiteSpace: 'nowrap' }} title="Все спины: живые деньги + фриспины + MoonStash">
                                всего {Number(game.spins_all).toLocaleString()}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell align="right">
                          {game.id === 3 || game.id === 6 ? (
                            /* Sweet Sperm / Moon Dogs - детальная разбивка с Buy Bonus */
                            <div style={{ lineHeight: 1.4 }}>
                              <div>
                                {Number((Number(game.bonuses_natural || 0) + Number(game.bonuses_buy || 0))).toLocaleString()}
                                <span style={{ color: '#9e9e9e', fontSize: '0.75em', marginLeft: 4 }}>
                                  ({Number(game.bonuses_buy || 0)}💰 / {Number(game.bonuses_natural || 0)}🔥)
                                </span>
                              </div>
                              {(game.retriggers_paid > 0 || game.retriggers_free > 0) && (
                                <div style={{ fontSize: '0.75em', color: '#e91e63' }}>
                                  R: {Number(game.retriggers_paid || 0)}💰 / {Number(game.retriggers_free || 0)}🔥
                                </div>
                              )}
                            </div>
                          ) : game.id === 4 ? (
                            /* v25.01: TON Rangers - T, D, DD бонусы */
                            <div style={{ lineHeight: 1.4 }}>
                              <div>
                                {Number(game.bonuses_natural || 0).toLocaleString()}
                              </div>
                              <div style={{ fontSize: '0.75em' }}>
                                <span style={{ color: '#f59e0b' }}>🚂T:{Number(game.train_heist_count || 0)}</span>
                                {' '}
                                <span style={{ color: '#8b5cf6' }}>⚔️D:{Number(game.star_duel_count || 0)}</span>
                                {Number(game.duel_retriggers || 0) > 0 && (
                                  <span style={{ color: '#9c27b0' }}>(R{game.duel_retriggers})</span>
                                )}
                                {' '}
                                <span style={{ color: '#ef4444' }}>💀DD:{Number(game.dead_star_count || 0)}</span>
                              </div>
                            </div>
                          ) : (
                            /* MoonToonz, TON Reel, Book of Moon - бонусы без ретриггеров */
                            <div style={{ lineHeight: 1.4 }}>
                              <div>
                                {Number(game.bonuses_natural || 0).toLocaleString()}
                                {game.retriggers > 0 && (
                                  <span style={{ color: '#e91e63', fontSize: '0.75em', marginLeft: 4 }}>
                                    (R{Number(game.retriggers)})
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                          {Number(game.bonuses_all || 0) > Number(game.bonuses_natural || 0) + Number(game.bonuses_buy || 0) && (
                            <div style={{ fontSize: '0.72em', color: '#94a3b8', whiteSpace: 'nowrap' }} title="Все бонусы: живые деньги + фриспины + MoonStash">
                              всего {Number(game.bonuses_all).toLocaleString()}
                            </div>
                          )}
                        </TableCell>
                        <TableCell align="right">{Number(game.wins).toLocaleString()}</TableCell>
                        <TableCell align="right">
                          {Number(game.gamble_total || 0) > 0 ? (
                            <div style={{ lineHeight: 1.4, fontSize: '0.8em' }}>
                              <div>{Number(game.gamble_total).toLocaleString()} игр</div>
                              <div style={{ color: '#4caf50' }}>W:{Number(game.gamble_wins || 0)} / L:{Number(game.gamble_losses || 0)}</div>
                              <div>Риск: {Number(game.gamble_risked || 0).toFixed(0)} → Нетто: <span style={{ color: Number(game.gamble_net || 0) > 0 ? '#f44336' : '#4caf50' }}>{Number(game.gamble_net || 0) > 0 ? '+' : ''}{Number(game.gamble_net || 0).toFixed(0)}</span></div>
                              <div style={{ color: '#ffa726' }}>{game.gamble_win_pct}% побед</div>
                            </div>
                          ) : (
                            <span style={{ color: '#666' }}>—</span>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {/* 11.10.2026: два RTP. Итог — все выплаты (с подарками лояльности) к реально заплаченному,
                              то есть RTP казино по деньгам. Слот — математика игры, покупка со скидкой по полной цене. */}
                          <div title="Итог: все выплаты, включая подарки лояльности, к тому, что игроки реально заплатили" style={{ whiteSpace: 'nowrap' }}>
                            <span style={{ fontSize: '0.72em', color: '#94a3b8', marginRight: 6 }}>итог</span>
                            <Chip
                              label={`${game.total_rtp}%`}
                              color={game.total_rtp > 98 ? 'error' : game.total_rtp > 94 ? 'success' : 'warning'}
                              size="small"
                            />
                          </div>
                          <div title="Слот: математика самой игры. Покупка бонуса со скидкой лояльности посчитана по полной цене" style={{ fontSize: '0.75em', color: '#cbd5e1', whiteSpace: 'nowrap', marginTop: 4 }}>
                            слот {game.actual_rtp}%
                          </div>
                          {Number(game.loyalty_gift || 0) > 0 && (
                            <div style={{ fontSize: '0.72em', color: '#94a3b8', whiteSpace: 'nowrap', marginTop: 2 }} title="Скидки и бесплатные покупки бонуса из лояльности: полная цена минус уплаченное">
                              🎁 лоялка дала {Number(game.loyalty_gift).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </div>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => handleResetStatsKeepLeaderboard(game.id, game.display_name)}
                            disabled={saving}
                            sx={{ fontSize: '0.65rem', py: 0.3, px: 1 }}
                          >
                            Сброс (без LB)
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* §владелец 13.07: Sugar Strike (USDT, отдельный бэк) — строкой ПОСЛЕ Авиатора,
                        чтобы все слоты и их RTP были видны наглядно в одном списке. Бонусы = боссы
                        (добито/появилось), выплаты в $, RTP-факт с своего sugar-стата. */}
                    {sugarStats?.periods?.all && (
                      <TableRow sx={{ bgcolor: 'rgba(255,122,209,0.06)' }}>
                        <TableCell>🍬 Sugar Strike{' '}
                          <span style={{ color: '#ff7ad1', fontSize: '0.75em' }}>USDT</span>
                        </TableCell>
                        <TableCell align="right">{Number(sugarStats.periods.all.shots).toLocaleString()}</TableCell>
                        <TableCell align="right">
                          <span style={{ fontSize: '0.78em', color: '#c9a2ff' }}>
                            бок {sugarStats.periods.all.sideKilled}/{sugarStats.periods.all.sideSpawned}
                            {' · '}верх {sugarStats.periods.all.topKilled}/{sugarStats.periods.all.topSpawned}
                          </span>
                        </TableCell>
                        <TableCell align="right">${Number(sugarStats.periods.all.payout).toLocaleString()}</TableCell>
                        <TableCell align="right"><span style={{ color: '#666' }}>—</span></TableCell>
                        <TableCell align="right">
                          <Chip
                            label={sugarStats.periods.all.rtp != null ? `${(sugarStats.periods.all.rtp * 100).toFixed(1)}%` : '—'}
                            color={sugarStats.periods.all.rtp > 0.95 ? 'error' : sugarStats.periods.all.rtp > 0.85 ? 'success' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Button size="small" variant="outlined" onClick={() => setTabValue(5)} sx={{ fontSize: '0.65rem', py: 0.3, px: 1 }}>
                            Открыть
                          </Button>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#94a3b8' }}>
                Цифра — на живые деньги. «всего» — вместе с фриспинами (промокоды, колесо, сундуки) и MoonStash.
              </Typography>

              {/* v25.01.26: Последние бонусные игры ПЕРЕД крупными выигрышами */}
              {recentBonuses.length > 0 && (
                <>
                  <Box sx={{ mt: 4, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      🎰 Последние бонусные игры — итог сессии (стр. {bonusPage}/{bonusTotalPages})
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel>Игра</InputLabel>
                      <Select
                        value={bonusGameFilter}
                        label="Игра"
                        onChange={(e) => {
                          const val = e.target.value;
                          setBonusGameFilter(val);
                          bonusGameFilterRef.current = val;
                          setBonusPage(1);
                          bonusPageRef.current = 1;
                          loadRecentBonuses(1, val);
                        }}
                      >
                        <MenuItem value="">Все игры</MenuItem>
                        <MenuItem value="moontoonz">MoonToonz</MenuItem>
                        <MenuItem value="tonreel">TON Reel</MenuItem>
                        <MenuItem value="sweetsperm">Sweet Sperm</MenuItem>
                        <MenuItem value="ton_ranger">TON Rangers</MenuItem>
                        <MenuItem value="book_of_moon">Book of Moon</MenuItem>
                        <MenuItem value="moondogs">Moon Dogs</MenuItem>
                        <MenuItem value="moon_hunter">Moon Hunter</MenuItem>
                        <MenuItem value="kaguya_ways">Kaguya Ways</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <TableContainer component={Paper} sx={{ bgcolor: 'rgba(156, 39, 176, 0.1)' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Игрок</TableCell>
                          <TableCell>Игра</TableCell>
                          <TableCell>Тип</TableCell>
                          <TableCell align="right">Ставка</TableCell>
                          <TableCell align="center">Спинов</TableCell>
                          <TableCell align="right">Итог бонуса</TableCell>
                          <TableCell align="right">Множитель</TableCell>
                          <TableCell>Дата</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recentBonuses.map((bonus) => (
                          <TableRow key={bonus.id} sx={{ bgcolor: bonus.isBuyBonus ? 'rgba(233, 30, 99, 0.1)' : 'transparent' }}>
                            <TableCell>{bonus.displayName}</TableCell>
                            <TableCell>{bonus.gameName}</TableCell>
                            <TableCell>
                              {bonus.isBuyBonus ? (
                                <Chip label="💰 Buy" size="small" sx={{ bgcolor: '#e91e63', color: 'white', fontSize: '0.7rem' }} />
                              ) : bonus.bonusType === 'train_heist' ? (
                                <Chip label="🚂 T" size="small" sx={{ bgcolor: '#f59e0b', color: 'white', fontSize: '0.7rem' }} />
                              ) : bonus.bonusType === 'star_duel' ? (
                                <Chip label="⚔️ D" size="small" sx={{ bgcolor: '#8b5cf6', color: 'white', fontSize: '0.7rem' }} />
                              ) : bonus.bonusType === 'dead_star' ? (
                                <Chip label="💀 DD" size="small" sx={{ bgcolor: '#ef4444', color: 'white', fontSize: '0.7rem' }} />
                              ) : (
                                <Chip label="🔥 Free" size="small" sx={{ bgcolor: '#ff6b35', color: 'white', fontSize: '0.7rem' }} />
                              )}
                            </TableCell>
                            <TableCell align="right">{bonus.bet.toFixed(2)}</TableCell>
                            <TableCell align="center">
                              <Chip
                                label={bonus.freeSpinsPlayed}
                                size="small"
                                sx={{ bgcolor: 'rgba(99, 102, 241, 0.3)', fontSize: '0.75rem', minWidth: 30 }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#10b981', fontWeight: 'bold' }}>
                              +{bonus.totalWin.toFixed(2)}
                            </TableCell>
                            <TableCell align="right" sx={{
                              fontWeight: 'bold',
                              color: bonus.multiplier >= 50 ? '#ffd700' : bonus.multiplier >= 20 ? '#e91e63' : bonus.multiplier >= 10 ? '#4caf50' : 'inherit'
                            }}>
                              x{bonus.multiplier}
                              {bonus.multiplier >= 100 && ' 🔥🔥'}
                              {bonus.multiplier >= 50 && bonus.multiplier < 100 && ' 🔥'}
                            </TableCell>
                            <TableCell>{new Date(bonus.date).toLocaleString('ru-RU')}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {bonusTotalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Pagination
                        count={bonusTotalPages}
                        page={bonusPage}
                        onChange={(e, newPage) => {
                          setBonusPage(newPage);
                          bonusPageRef.current = newPage;
                          loadRecentBonuses(newPage);
                        }}
                        color="primary"
                        size="small"
                      />
                    </Box>
                  )}
                </>
              )}

              {/* v25.01.26: Крупные выигрыши теперь ПОСЛЕ бонусных игр */}
              {stats.bigWins?.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                    Последние крупные выигрыши (10)
                  </Typography>
                  <TableContainer component={Paper} sx={{ bgcolor: 'rgba(30, 41, 59, 0.5)' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Игрок</TableCell>
                          <TableCell>Игра</TableCell>
                          <TableCell align="right">Ставка</TableCell>
                          <TableCell align="right">Выигрыш</TableCell>
                          <TableCell>Дата</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stats.bigWins.slice(0, 10).map((win) => (
                          <TableRow key={win.id}>
                            <TableCell>{win.username || 'Аноним'}</TableCell>
                            <TableCell>{win.game_name}</TableCell>
                            <TableCell align="right">{Number(win.bet_amount).toFixed(2)}</TableCell>
                            <TableCell align="right" sx={{ color: Number(win.jackpot_amount) > 0 ? '#f59e0b' : '#10b981', fontWeight: 'bold' }}>
                              +{Number(win.jackpot_amount) > 0 ? Number(win.jackpot_amount).toLocaleString() : Number(win.win_amount).toLocaleString()}
                              {Number(win.jackpot_amount) > 0 && ' 🎰 JP'}
                            </TableCell>
                            <TableCell>{new Date(win.created_at).toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Последние джекпоты */}
              {jackpotWinners?.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mt: 4, mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <EmojiEvents color="warning" /> Последние джекпоты
                    {currentJackpotInfo && currentJackpotInfo.cooldownSpins != null && (
                      editingCooldown ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginLeft: '8px' }}>
                          <TextField
                            size="small"
                            type="number"
                            value={cooldownInput}
                            onChange={e => setCooldownInput(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') saveJackpotCooldown(); if (e.key === 'Escape') setEditingCooldown(false); }}
                            autoFocus
                            sx={{ width: '130px', '& input': { py: '4px', fontSize: '0.8rem' } }}
                            placeholder="Спинов"
                          />
                          <Button size="small" variant="contained" color="warning" onClick={saveJackpotCooldown} sx={{ minWidth: '30px', px: 1, py: '3px', fontSize: '0.7rem' }}>OK</Button>
                          <Button size="small" onClick={() => setEditingCooldown(false)} sx={{ minWidth: '30px', px: 1, py: '3px', fontSize: '0.7rem' }}>✕</Button>
                        </span>
                      ) : (
                        <span
                          onClick={() => { setCooldownInput(String(currentJackpotInfo.cooldownSpins)); setEditingCooldown(true); }}
                          style={{
                            fontSize: '0.75rem',
                            padding: '2px 10px',
                            borderRadius: '12px',
                            marginLeft: '8px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            background: currentJackpotInfo.cooldownSpins === 0 ? 'rgba(76,175,80,0.15)' : 'rgba(255,152,0,0.15)',
                            color: currentJackpotInfo.cooldownSpins === 0 ? '#4caf50' : '#ff9800',
                            border: `1px solid ${currentJackpotInfo.cooldownSpins === 0 ? 'rgba(76,175,80,0.3)' : 'rgba(255,152,0,0.3)'}`
                          }}
                          title="Кликни чтобы изменить КД"
                        >
                          {currentJackpotInfo.cooldownSpins === 0
                            ? '✅ КД: готов!'
                            : `🔒 КД: ${currentJackpotInfo.cooldownSpins.toLocaleString()} спинов`}
                          {' ✏️'}
                        </span>
                      )
                    )}
                    {currentJackpotInfo && currentJackpotInfo.current != null && (
                      <span style={{
                        fontSize: '0.75rem',
                        padding: '2px 10px',
                        borderRadius: '12px',
                        marginLeft: '8px',
                        fontWeight: 'bold',
                        background: 'rgba(255, 193, 7, 0.15)',
                        color: '#ffc107',
                        border: '1px solid rgba(255, 193, 7, 0.3)'
                      }}>
                        💰 Пул: {Number(currentJackpotInfo.current).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} SPERM
                      </span>
                    )}
                  </Typography>
                  <TableContainer component={Paper} sx={{ bgcolor: 'rgba(255, 193, 7, 0.1)' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Игрок</TableCell>
                          <TableCell>Telegram ID</TableCell>
                          <TableCell>Игра</TableCell>
                          <TableCell align="right">Ставка</TableCell>
                          <TableCell align="right">Джекпот</TableCell>
                          <TableCell>Дата</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {jackpotWinners.map((jp, idx) => (
                          <TableRow key={idx} sx={{ bgcolor: 'rgba(255, 193, 7, 0.05)' }}>
                            <TableCell><strong>{jp.displayName}</strong></TableCell>
                            <TableCell>{jp.telegramId}</TableCell>
                            <TableCell>{jp.gameName}</TableCell>
                            <TableCell align="right">{jp.betAmount?.toFixed(2) || '0.00'}</TableCell>
                            <TableCell align="right" sx={{ color: '#ffc107', fontWeight: 'bold' }}>
                              +{(jp.jackpotAmount || jp.totalWin || 0).toFixed(2)} SPERM
                            </TableCell>
                            <TableCell>{new Date(jp.date).toLocaleString('ru-RU')}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* v26.02.08: Gamble лидерборд */}
              {gambleLeaderboard.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mt: 4, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    🃏 Gamble лидерборд
                    <Chip label={`${gambleLeaderboard.length} игроков`} size="small" variant="outlined" />
                  </Typography>
                  <TableContainer component={Paper} sx={{ bgcolor: 'rgba(233, 30, 99, 0.08)' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>Игрок</TableCell>
                          <TableCell align="right">Попыток</TableCell>
                          <TableCell align="right">Побед</TableCell>
                          <TableCell align="right">Win%</TableCell>
                          <TableCell align="right">Выиграно</TableCell>
                          <TableCell align="right">Проиграно</TableCell>
                          <TableCell align="right">Нетто</TableCell>
                          <TableCell align="center">Макс серия</TableCell>
                          <TableCell align="right">Макс выигрыш</TableCell>
                          <TableCell align="right">Макс ставка</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {gambleLeaderboard.map((player, idx) => (
                          <TableRow key={player.userId} sx={{
                            bgcolor: idx === 0 ? 'rgba(255, 215, 0, 0.1)' : idx === 1 ? 'rgba(192, 192, 192, 0.08)' : idx === 2 ? 'rgba(205, 127, 50, 0.08)' : 'transparent'
                          }}>
                            <TableCell sx={{ fontWeight: 'bold', color: idx < 3 ? '#ffd700' : 'inherit' }}>
                              {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}
                            </TableCell>
                            <TableCell><strong>{player.displayName}</strong></TableCell>
                            <TableCell align="right">{player.totalGambles}</TableCell>
                            <TableCell align="right" sx={{ color: '#4caf50' }}>{player.totalWins}</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${player.winRate}%`}
                                size="small"
                                sx={{
                                  bgcolor: player.winRate >= 55 ? 'rgba(76,175,80,0.2)' : player.winRate >= 45 ? 'rgba(255,152,0,0.2)' : 'rgba(244,67,54,0.2)',
                                  fontSize: '0.7rem',
                                  height: 20
                                }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                              +{player.totalWon.toFixed(2)}
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#ef5350' }}>
                              -{player.totalLost.toFixed(2)}
                            </TableCell>
                            <TableCell align="right" sx={{
                              color: player.netProfit >= 0 ? '#4caf50' : '#ef5350',
                              fontWeight: 'bold'
                            }}>
                              {player.netProfit >= 0 ? '+' : ''}{player.netProfit.toFixed(2)}
                            </TableCell>
                            <TableCell align="center">
                              {player.maxWinStreak > 0 && (
                                <Chip
                                  label={`${player.maxWinStreak}x`}
                                  size="small"
                                  sx={{
                                    bgcolor: player.maxWinStreak >= 5 ? 'rgba(233,30,99,0.3)' : 'rgba(99,102,241,0.2)',
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold',
                                    height: 20
                                  }}
                                />
                              )}
                            </TableCell>
                            <TableCell align="right">{player.maxSingleWin.toFixed(2)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', color: '#ff9800' }}>{player.maxBet.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          )}
        </TabPanel>


        {/* ТАБ 2: Пользователи — итоги за сутки: профит + топ-10/топ-10 */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            <Typography variant="h6">
              Игроки за сутки
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => { loadTopPlayersByDate(topPlayersDate); loadJackpotWinners(); }}
            >
              Обновить
            </Button>
          </Box>

          {/* Информация о джекпоте */}
          {currentJackpotInfo && (
            <Alert severity="info" icon={<EmojiEvents />} sx={{ mb: 3 }}>
              <strong>Grand Jackpot:</strong> {currentJackpotInfo.current.toFixed(2)} SPERM |
              <strong> Последний выигрыш:</strong> {currentJackpotInfo.lastWonAmount.toFixed(2)} SPERM ({currentJackpotInfo.lastWonBy}) |
              <strong> Всего выиграно:</strong> {currentJackpotInfo.totalWonCount} раз
            </Alert>
          )}

          {/* v26.02.07: Топ 10 игроков с навигацией по дням */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUp color="primary" /> Топ 10 игроков за день
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  size="small"
                  disabled={topPlayersDayIndex >= topPlayersAvailableDays.length - 1}
                  onClick={() => {
                    const newIdx = topPlayersDayIndex + 1;
                    setTopPlayersDayIndex(newIdx);
                    const day = topPlayersAvailableDays[newIdx];
                    setTopPlayersDate(day);
                    loadTopPlayersByDate(day);
                  }}
                >
                  <NavigateBefore />
                </IconButton>
                <Chip
                  label={
                    topPlayersDayIndex === 0
                      ? 'Сегодня'
                      : topPlayersAvailableDays[topPlayersDayIndex]
                        ? new Date(topPlayersAvailableDays[topPlayersDayIndex]).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' })
                        : '—'
                  }
                  color={topPlayersDayIndex === 0 ? 'primary' : 'default'}
                  variant={topPlayersDayIndex === 0 ? 'filled' : 'outlined'}
                />
                <IconButton
                  size="small"
                  disabled={topPlayersDayIndex <= 0}
                  onClick={() => {
                    const newIdx = topPlayersDayIndex - 1;
                    setTopPlayersDayIndex(newIdx);
                    if (newIdx === 0) {
                      setTopPlayersDate(null);
                      loadTopPlayersByDate(null);
                    } else {
                      const day = topPlayersAvailableDays[newIdx];
                      setTopPlayersDate(day);
                      loadTopPlayersByDate(day);
                    }
                  }}
                >
                  <NavigateNext />
                </IconButton>
                <Button size="small" variant="outlined" onClick={() => loadTopPlayersByDate(topPlayersDate)} startIcon={<Refresh />}>
                  Обновить
                </Button>
              </Box>
            </Box>
            {/* Итоги казино за сутки — два крупных числа: GGR и чистый профит */}
            {dayTotal && (
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(99, 102, 241, 0.2)' }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Итоги казино за сутки (с 00:00 МСК)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: dayTotal.ngr >= 0 ? 'rgba(76, 175, 80, 0.10)' : 'rgba(244, 67, 54, 0.10)' }}>
                      <Typography variant="caption" color="text.secondary">
                        Игровой итог · ставки − выигрыши − отыгранная лоялка
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: dayTotal.ngr >= 0 ? '#4caf50' : '#f44336' }}>
                        {dayTotal.ngr >= 0 ? '+' : ''}{dayTotal.ngr.toLocaleString(undefined, { maximumFractionDigits: 0 })} SPERM
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Ставки − выигрыши: {dayTotal.ggr >= 0 ? '+' : ''}{dayTotal.ggr.toLocaleString(undefined, { maximumFractionDigits: 0 })} ·
                        лоялка стала живой: −{dayTotal.bonusUnlocked.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: dayTotal.netProfit >= 0 ? 'rgba(76, 175, 80, 0.10)' : 'rgba(244, 67, 54, 0.10)' }}>
                      <Typography variant="caption" color="text.secondary">
                        Чистый профит · депозиты − выводы (все расходы)
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: dayTotal.netProfit >= 0 ? '#4caf50' : '#f44336' }}>
                        {dayTotal.netProfit >= 0 ? '+' : ''}{dayTotal.netProfit.toLocaleString(undefined, { maximumFractionDigits: 0 })} SPERM
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Депозиты: {dayTotal.deposits.toLocaleString(undefined, { maximumFractionDigits: 0 })} ·
                  Выводы: {dayTotal.withdrawals.toLocaleString(undefined, { maximumFractionDigits: 0 })} ·
                  Спинов: {dayTotal.spins.toLocaleString()} · Игроков: {dayTotal.uniquePlayers}.
                  Лоялти/кэшбэк/гонки учтены в чистом (стоят нам, когда выведены).
                </Typography>
              </Paper>
            )}

            {/* Топ-10 — минуснули нас (мы в минусе, слили им) — ВВЕРХУ */}
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5, color: '#f44336' }}>
              <TrendingDown fontSize="small" /> Топ-10 — минуснули нас (мы в минусе)
            </Typography>
            {renderTopPlayersTable(topPlayersByDate, 'Нет игроков за выбранный день')}

            {/* Топ-10 — принесли нам прибыль (мы в плюсе с них) */}
            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5, color: '#4caf50' }}>
              <TrendingUp fontSize="small" /> Топ-10 — принесли нам прибыль (мы в плюсе)
            </Typography>
            {renderTopPlayersTable(topProfitPlayers, 'Нет игроков за выбранный день')}            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Обе колонки — глазами КАЗИНО (нас):
              <span style={{ color: '#4caf50' }}> 🟢 «+» зелёный = МЫ в плюсе с юзера</span> ·
              <span style={{ color: '#f44336' }}> 🔴 «−» красный = МЫ в минусе (слили ему)</span>.
              <br /><b>«Наш P&L (день)»</b> — касса за сегодня.
              <b> «Наш P&L (всего)»</b> — касса за всё время: занёс − вывел − что ещё может забрать
              (свободный баланс, копилка, заявки в ожидании, звёздный стейк). Лоялти в локе не считается.
            </Typography>
          </Box>

        </TabPanel>

        {/* ТАБ 3: Аналитика — детальные данные (ленивая загрузка) */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            <Typography variant="h6">
              Детальная аналитика
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => { loadProfitHistory(profitHistoryPage); loadSlotUsers(); }}
            >
              Обновить
            </Button>
          </Box>

          {/* v26.02.07: Сводка по периодам — 1 мес / 3 мес / 6 мес */}
          {profitSummaries && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarMonth fontSize="small" /> Сводка по периодам
              </Typography>
              <Grid container spacing={2}>
                {['1m', '3m', '6m'].map((key) => {
                  const s = profitSummaries[key];
                  if (!s) return null;
                  const profitColor = s.profit > 0 ? '#4caf50' : s.profit < 0 ? '#f44336' : 'inherit';
                  return (
                    <Grid item xs={12} md={4} key={key}>
                      <Paper sx={{ p: 2, bgcolor: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(99, 102, 241, 0.2)' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#6366f1', fontWeight: 'bold' }}>
                          {s.label}
                        </Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Спинов</Typography>
                            <Typography variant="body2" fontWeight="bold">{s.totalSpins.toLocaleString()}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Игроков</Typography>
                            <Typography variant="body2" fontWeight="bold">{s.uniquePlayers}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Поставлено</Typography>
                            <Typography variant="body2">{s.totalBets.toLocaleString(undefined, {maximumFractionDigits: 0})}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Выплачено</Typography>
                            <Typography variant="body2">{s.totalWins.toLocaleString(undefined, {maximumFractionDigits: 0})}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Профит казино</Typography>
                            <Typography variant="body2" fontWeight="bold" sx={{ color: profitColor }}>
                              {s.profit > 0 ? '+' : ''}{s.profit.toLocaleString(undefined, {maximumFractionDigits: 0})} SPERM
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">RTP</Typography>
                            <Typography variant="body2">
                              <Chip
                                label={`${s.rtp}%`}
                                size="small"
                                color={s.rtp > 98 ? 'error' : s.rtp > 94 ? 'success' : 'warning'}
                              />
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Джекпотов</Typography>
                            <Typography variant="body2">{s.jackpots}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Бонусов (без R)</Typography>
                            <Typography variant="body2">
                              {(s.bonuses_natural || 0).toLocaleString()}
                              {(s.retriggers || 0) > 0 && (
                                <span style={{ color: '#e91e63', fontSize: '0.8em' }}> (R{s.retriggers})</span>
                              )}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}

          {/* v26.02.07: Профит по дням с пагинацией */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <BarChart fontSize="small" /> Профит по дням (SPERM)
            </Typography>
            {profitHistory.length > 0 && (
              <>
                <TableContainer component={Paper} sx={{ bgcolor: 'rgba(30, 41, 59, 0.5)', mb: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Дата</TableCell>
                        <TableCell align="right">Спинов</TableCell>
                        <TableCell align="right">Игроков</TableCell>
                        <TableCell align="right">Поставлено</TableCell>
                        <TableCell align="right">Выплачено</TableCell>
                        <TableCell align="right">Профит</TableCell>
                        <TableCell align="right">RTP</TableCell>
                        <TableCell align="right">Макс. вин</TableCell>
                        <TableCell align="center">JP</TableCell>
                        <TableCell align="center">Бонусы / R</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {profitHistory.map((day) => {
                        const profitColor = day.profit > 0 ? '#4caf50' : day.profit < 0 ? '#f44336' : 'inherit';
                        const dateStr = new Date(day.date).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
                        const isToday = new Date(day.date).toDateString() === new Date().toDateString();
                        return (
                          <TableRow key={day.date} sx={{ bgcolor: isToday ? 'rgba(99, 102, 241, 0.1)' : 'transparent' }}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {dateStr}
                                {isToday && <Chip label="Сегодня" size="small" color="primary" sx={{ fontSize: '0.65rem', height: 18 }} />}
                              </Box>
                            </TableCell>
                            <TableCell align="right">{day.totalSpins.toLocaleString()}</TableCell>
                            <TableCell align="right">{day.uniquePlayers}</TableCell>
                            <TableCell align="right">{day.totalBets.toLocaleString(undefined, {maximumFractionDigits: 0})}</TableCell>
                            <TableCell align="right">{day.totalWins.toLocaleString(undefined, {maximumFractionDigits: 0})}</TableCell>
                            <TableCell align="right" sx={{ color: profitColor, fontWeight: 'bold' }}>
                              {day.profit > 0 ? '+' : ''}{day.profit.toLocaleString(undefined, {maximumFractionDigits: 0})}
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${day.rtp}%`}
                                size="small"
                                color={day.rtp > 98 ? 'error' : day.rtp > 94 ? 'success' : 'warning'}
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            </TableCell>
                            <TableCell align="right">{day.maxWin.toFixed(2)}</TableCell>
                            <TableCell align="center">{day.jackpots > 0 ? day.jackpots : '—'}</TableCell>
                            <TableCell align="center">
                              {day.bonuses_natural || 0}
                              {(day.retriggers || 0) > 0 && (
                                <span style={{ color: '#e91e63', fontSize: '0.8em' }}> (R{day.retriggers})</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                {profitHistoryTotalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                    <Pagination
                      count={profitHistoryTotalPages}
                      page={profitHistoryPage + 1}
                      onChange={(e, newPage) => {
                        setProfitHistoryPage(newPage - 1);
                        loadProfitHistory(newPage - 1);
                      }}
                      color="primary"
                      size="small"
                    />
                  </Box>
                )}
              </>
            )}
          </Box>

          {/* Таблица всех пользователей */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <People /> Все игроки ({slotUsersTotal})
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Сортировка: {
                {
                  displayName: 'Имя',
                  telegramId: 'Telegram ID',
                  totalSpins: 'Спины',
                  totalBets: 'Ставки',
                  totalWins: 'Выигрыши',
                  realPnlCasino: 'Реальный P&L (касса)',
                  netProfitCasino: 'Слот-эдж',
                  rtpPercent: 'RTP%',
                  maxWin: 'Макс. выигрыш',
                  jackpotWins: 'Джекпоты',
                  lastPlayed: 'Последняя игра'
                }[sortField] || sortField
              } {sortDirection === 'asc' ? '↑' : '↓'}
            </Typography>
          </Box>
          <TableContainer component={Paper} sx={{ bgcolor: 'rgba(30, 41, 59, 0.5)' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <SortableHeader field="displayName">Пользователь</SortableHeader>
                  <SortableHeader field="telegramId">Telegram ID</SortableHeader>
                  <SortableHeader field="totalSpins" align="right">Спины</SortableHeader>
                  <SortableHeader field="totalBets" align="right">Ставки</SortableHeader>
                  <SortableHeader field="totalWins" align="right">Выигрыши</SortableHeader>
                  <SortableHeader field="realPnlCasino" align="right">Реальный P&L</SortableHeader>
                  <SortableHeader field="netProfitCasino" align="right">Слот-эдж</SortableHeader>
                  <SortableHeader field="rtpPercent" align="right">RTP%</SortableHeader>
                  <SortableHeader field="maxWin" align="right">Макс. выигрыш</SortableHeader>
                  <SortableHeader field="jackpotWins" align="center">Джекпоты</SortableHeader>
                  <SortableHeader field="lastPlayed">Последняя игра</SortableHeader>
                  <TableCell sx={{ px: 0.5, minWidth: 40 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedUsers.map((user) => (
                  <React.Fragment key={user.id}>
                    <TableRow
                      hover
                      sx={{ '& > *': { borderBottom: expandedUserId === user.id ? 'unset' : undefined } }}
                    >
                      <TableCell>
                        <Typography
                          component="span"
                          sx={{ fontWeight: 'bold', cursor: 'pointer', '&:hover': { color: '#6366f1', textDecoration: 'underline' } }}
                          onClick={() => openGambleMenu(user.id)}
                        >
                          {user.displayName}
                        </Typography>
                        {user.username && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            @{user.username}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{user.telegramId}</TableCell>
                      <TableCell align="right">{user.totalSpins}</TableCell>
                      <TableCell align="right">{user.totalBets.toFixed(2)}</TableCell>
                      <TableCell align="right" sx={{ color: user.totalWins > 0 ? '#4caf50' : 'inherit' }}>
                        {user.totalWins.toFixed(2)}
                      </TableCell>
                      <TableCell align="right" sx={{
                        color: (user.realPnlCasino ?? 0) > 0 ? '#4caf50' : (user.realPnlCasino ?? 0) < 0 ? '#f44336' : 'inherit',
                        fontWeight: 'bold'
                      }}>
                        {(user.realPnlCasino ?? 0) > 0 ? '+' : ''}{(user.realPnlCasino ?? 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="right" sx={{
                        color: user.netProfitCasino > 0 ? '#4caf50' : '#f44336'
                      }}>
                        {user.netProfitCasino > 0 ? '+' : ''}{user.netProfitCasino.toFixed(2)}
                      </TableCell>
                      <TableCell align="right" sx={{
                        color: user.rtpPercent > 100 ? '#f44336' : user.rtpPercent > 80 ? '#ff9800' : '#4caf50'
                      }}>
                        {user.rtpPercent.toFixed(1)}%
                      </TableCell>
                      <TableCell align="right">
                        {user.maxWin > 0 ? user.maxWin.toFixed(2) : '—'}
                      </TableCell>
                      <TableCell align="center">
                        {user.jackpotWins > 0 ? (
                          <Chip label={user.jackpotWins} color="warning" size="small" icon={<EmojiEvents />} />
                        ) : '—'}
                      </TableCell>
                      <TableCell>
                        {user.lastPlayed ? new Date(user.lastPlayed).toLocaleString('ru-RU') : '—'}
                      </TableCell>
                      {/* стрелка «детали» — лаконичная золотая кнопка (владелец 01.10). На телефоне была невидима
                          из-за svg { max-width: 100% } в index.css (иконка сплющивалась в сжатой колонке) */}
                      <TableCell sx={{ px: 0.5, minWidth: 40 }}>
                        <IconButton
                          size="small"
                          sx={{ width: 28, height: 28, flexShrink: 0, color: '#fbbf24', border: '1px solid rgba(251,191,36,0.45)', '&:hover': { bgcolor: 'rgba(251,191,36,0.12)' }, '& svg': { maxWidth: 'none', width: 20, height: 20 } }}
                          onClick={() => toggleUserDetails(user.id)}
                          title={expandedUserId === user.id ? 'Скрыть детали' : 'Показать детали'}
                        >
                          {expandedUserId === user.id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {/* Раскрывающиеся детали */}
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
                        <Collapse in={expandedUserId === user.id} timeout="auto" unmountOnExit>
                          {expandedUserId === user.id && userDetails && (
                            <Box sx={{ py: 2, px: 1 }}>
                              <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={3}>
                                  <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'rgba(30, 41, 59, 0.5)' }}>
                                    <Typography variant="caption" color="text.secondary">Telegram ID</Typography>
                                    <Typography variant="body2" fontWeight="bold">{userDetails.user.telegramId}</Typography>
                                  </Paper>
                                </Grid>
                                <Grid item xs={3}>
                                  <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'rgba(30, 41, 59, 0.5)' }}>
                                    <Typography variant="caption" color="text.secondary">Баланс</Typography>
                                    <Typography variant="body2" fontWeight="bold">{userDetails.user.balance.toFixed(2)} SPERM</Typography>
                                  </Paper>
                                </Grid>
                                <Grid item xs={3}>
                                  <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'rgba(30, 41, 59, 0.5)' }}>
                                    <Typography variant="caption" color="text.secondary">Всего спинов</Typography>
                                    <Typography variant="body2" fontWeight="bold">{userDetails.gameStats.reduce((s, g) => s + g.spins, 0)}</Typography>
                                  </Paper>
                                </Grid>
                                <Grid item xs={3}>
                                  <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'rgba(30, 41, 59, 0.5)' }}>
                                    <Typography variant="caption" color="text.secondary">Джекпотов</Typography>
                                    <Typography variant="body2" fontWeight="bold">{userDetails.jackpots?.length || 0}</Typography>
                                  </Paper>
                                </Grid>
                              </Grid>

                              {/* 🎯 Объективный P&L (касса) — главное число */}
                              <CashTruthBlock user={userDetails.user} />

                              {/* Откуда профит: разбивка по слотам и источникам */}
                              {userDetails.gameStats.length > 0 && <ProfitBreakdown gameStats={userDetails.gameStats} />}

                              {/* Лояльность сегодня: разбивка по источникам */}
                              {userDetails.loyaltyToday && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block', color: '#9c27b0' }}>
                                    💎 Лояльность сегодня
                                  </Typography>
                                  <Paper sx={{ p: 1.5, bgcolor: 'rgba(156, 39, 176, 0.08)', mb: 1 }}>
                                    <Grid container spacing={1}>
                                      <Grid item xs={4}>
                                        <Typography variant="caption" color="text.secondary">Профит со слотов</Typography>
                                        <Typography variant="body2" fontWeight="bold" sx={{ color: userDetails.loyaltyToday.slotProfit >= 0 ? '#4caf50' : '#f44336' }}>
                                          {userDetails.loyaltyToday.slotProfit >= 0 ? '+' : ''}{userDetails.loyaltyToday.slotProfit.toFixed(2)} SPERM
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={4}>
                                        <Typography variant="caption" color="text.secondary">Wager отыграно (live)</Typography>
                                        <Typography variant="body2" fontWeight="bold" sx={{ color: '#4caf50' }}>
                                          +{userDetails.loyaltyToday.wagerUnlockedTotal.toFixed(2)} SPERM
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={4}>
                                        <Typography variant="caption" color="text.secondary">Итого live-профит</Typography>
                                        <Typography variant="body2" fontWeight="bold" sx={{ color: userDetails.loyaltyToday.livePnLTotal >= 0 ? '#4caf50' : '#f44336' }}>
                                          {userDetails.loyaltyToday.livePnLTotal >= 0 ? '+' : ''}{userDetails.loyaltyToday.livePnLTotal.toFixed(2)} SPERM
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  </Paper>

                                  {userDetails.loyaltyToday.wagerUnlocks.length > 0 && (
                                    <Box sx={{ mb: 1 }}>
                                      <Typography variant="caption" sx={{ color: '#4caf50' }}>
                                        ✅ Разблокировано отыгрышем (зачислено в баланс):
                                      </Typography>
                                      <Table size="small" sx={{ bgcolor: 'rgba(76, 175, 80, 0.05)' }}>
                                        <TableBody>
                                          {userDetails.loyaltyToday.wagerUnlocks.map((u) => (
                                            <TableRow key={`unlock2-${u.source}`}>
                                              <TableCell sx={{ py: 0.3 }}>{u.source}</TableCell>
                                              <TableCell align="right" sx={{ py: 0.3 }}>× {u.count}</TableCell>
                                              <TableCell align="right" sx={{ py: 0.3, color: '#4caf50', fontWeight: 'bold' }}>+{u.amount.toFixed(2)} SPERM</TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </Box>
                                  )}

                                  {userDetails.loyaltyToday.pendingCredits.length > 0 && (
                                    <Box>
                                      <Typography variant="caption" sx={{ color: '#ff9800' }}>
                                        ⏳ В MoonStash (под отыгрышем, пока недоступно):
                                      </Typography>
                                      <Table size="small" sx={{ bgcolor: 'rgba(255, 152, 0, 0.05)' }}>
                                        <TableBody>
                                          {userDetails.loyaltyToday.pendingCredits.map((c) => (
                                            <TableRow key={`pending2-${c.source}`}>
                                              <TableCell sx={{ py: 0.3 }}>{c.source}</TableCell>
                                              <TableCell align="right" sx={{ py: 0.3 }}>× {c.count}</TableCell>
                                              <TableCell align="right" sx={{ py: 0.3, color: '#ff9800' }}>+{c.amount.toFixed(2)} SPERM</TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </Box>
                                  )}
                                </Box>
                              )}

                              {/* Топ 10 заносов сегодня */}
                              {userDetails.todayBigWins && userDetails.todayBigWins.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block', color: '#ffc107' }}>
                                    Топ 10 заносов сегодня (по иксам):
                                  </Typography>
                                  <Table size="small" sx={{ bgcolor: 'rgba(255, 193, 7, 0.1)' }}>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={{ py: 0.5 }}>#</TableCell>
                                        <TableCell sx={{ py: 0.5 }}>Игра</TableCell>
                                        <TableCell align="right" sx={{ py: 0.5 }}>Ставка</TableCell>
                                        <TableCell align="right" sx={{ py: 0.5 }}>Выигрыш</TableCell>
                                        <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold' }}>x</TableCell>
                                        <TableCell sx={{ py: 0.5 }}>Время</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {userDetails.todayBigWins.map((bw, idx) => (
                                        <TableRow key={bw.id}>
                                          <TableCell sx={{ py: 0.5, fontWeight: 'bold', color: idx < 3 ? '#ffc107' : 'inherit' }}>{idx + 1}</TableCell>
                                          <TableCell sx={{ py: 0.5 }}>{bw.gameName}</TableCell>
                                          <TableCell align="right" sx={{ py: 0.5 }}>{bw.bet.toFixed(2)}</TableCell>
                                          <TableCell align="right" sx={{ py: 0.5, color: '#4caf50', fontWeight: 'bold' }}>{bw.win.toFixed(2)}</TableCell>
                                          <TableCell align="right" sx={{ py: 0.5, color: '#ffc107', fontWeight: 'bold' }}>x{bw.multiplier}</TableCell>
                                          <TableCell sx={{ py: 0.5 }}>{new Date(bw.date).toLocaleTimeString('ru-RU')}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              )}

                              {/* Большие выигрыши */}
                              {userDetails.bigWins.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block', color: '#4caf50' }}>
                                    <TrendingUp sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                                    Большие выигрыши (x10+):
                                  </Typography>
                                  <Table size="small" sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)' }}>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={{ py: 0.5 }}>Игра</TableCell>
                                        <TableCell align="right" sx={{ py: 0.5 }}>Ставка</TableCell>
                                        <TableCell align="right" sx={{ py: 0.5 }}>Выигрыш</TableCell>
                                        <TableCell align="right" sx={{ py: 0.5 }}>x</TableCell>
                                        <TableCell sx={{ py: 0.5 }}>Дата</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {userDetails.bigWins.slice(0, 5).map((bw) => (
                                        <TableRow key={bw.id}>
                                          <TableCell sx={{ py: 0.5 }}>{bw.gameName}</TableCell>
                                          <TableCell align="right" sx={{ py: 0.5 }}>{bw.bet.toFixed(2)}</TableCell>
                                          <TableCell align="right" sx={{ py: 0.5, color: '#4caf50', fontWeight: 'bold' }}>{bw.win.toFixed(2)}</TableCell>
                                          <TableCell align="right" sx={{ py: 0.5 }}>x{bw.multiplier}</TableCell>
                                          <TableCell sx={{ py: 0.5 }}>{new Date(bw.date).toLocaleString('ru-RU')}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              )}

                              {/* Последние спины */}
                              <Box>
                                <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>Последние 10 спинов:</Typography>
                                <Table size="small" sx={{ bgcolor: 'rgba(30, 41, 59, 0.3)' }}>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ py: 0.5 }}>Игра</TableCell>
                                      <TableCell align="right" sx={{ py: 0.5 }}>Ставка</TableCell>
                                      <TableCell align="right" sx={{ py: 0.5 }}>Выигрыш</TableCell>
                                      <TableCell sx={{ py: 0.5 }}>Дата</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {userDetails.recentSpins.slice(0, 10).map((spin) => (
                                      <TableRow key={spin.id}>
                                        <TableCell sx={{ py: 0.5 }}>{spin.gameName}</TableCell>
                                        <TableCell align="right" sx={{ py: 0.5 }}>{spin.bet.toFixed(2)}</TableCell>
                                        <TableCell align="right" sx={{ py: 0.5, color: spin.win > 0 ? '#4caf50' : 'inherit' }}>{spin.win.toFixed(2)}</TableCell>
                                        <TableCell sx={{ py: 0.5 }}>{new Date(spin.date).toLocaleString('ru-RU')}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Box>
                          )}
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Пагинация для таблицы "Все игроки" */}
          <TablePagination
            component="div"
            count={slotUsersTotal}
            page={usersPage}
            onPageChange={handleUsersPageChange}
            rowsPerPage={usersPerPage}
            onRowsPerPageChange={handleUsersPerPageChange}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage="Игроков на странице:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count !== -1 ? count : `более чем ${to}`}`}
            sx={{
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontSize: '0.875rem'
              }
            }}
          />

        </TabPanel>

        {/* ТАБ 4: Авиатор (crash-игра, слот №7) */}
        <TabPanel value={tabValue} index={4}>
          <AviatorAdmin />
        </TabPanel>

        {/* ТАБ 5: Sugar Strike (fish-shooter, USDT) */}
        <TabPanel value={tabValue} index={5}>
          <SugarStrikeAdmin />
        </TabPanel>

        {/* ТАБ 6: Moon Plinko (мини-игра, /plinko в боте) */}
        <TabPanel value={tabValue} index={6}>
          <PlinkoAdmin />
        </TabPanel>

        {/* ТАБ 7: Moon Ladder (мини-игра «лесенка», /ladder в боте) */}
        <TabPanel value={tabValue} index={7}>
          <LadderAdmin />
        </TabPanel>

        {/* ТАБ 8: Wiggle Wars — бои против ботов, ручки регулятора */}
        <TabPanel value={tabValue} index={8}>
          <WiggleAdmin />
        </TabPanel>

        {/* 📢 УВЕДОМЛЕНИЕ В ЛОББИ */}
        <TabPanel value={tabValue} index={ANN_TAB_INDEX}>
          <Box sx={{ maxWidth: 760 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Typography variant="h6">Важное уведомление в лобби</Typography>
              {annMeta && (
                <Chip
                  size="small"
                  label={annForm.enabled ? (annForm.testOnly ? '🧪 Тест: видишь только ты' : '🟢 Видят все') : '⚪ Выключено'}
                  color={annForm.enabled ? (annForm.testOnly ? 'warning' : 'success') : 'default'}
                  variant="outlined"
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Модалка поверх лобби слотов: игрок не сможет ничего нажать, пока не
              подтвердит кнопкой OK. Каждое сохранение показывает уведомление заново
              даже тем, кто уже нажимал OK.
            </Typography>

            {!annLoaded ? (
              <CircularProgress size={28} />
            ) : (
              <Paper sx={{ p: { xs: 1.5, sm: 3 } }}>
                <TextField
                  label="Заголовок"
                  value={annForm.title}
                  onChange={(e) => setAnnForm({ ...annForm, title: e.target.value })}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Текст"
                  value={annForm.body}
                  onChange={(e) => setAnnForm({ ...annForm, body: e.target.value })}
                  fullWidth
                  multiline
                  minRows={4}
                  helperText="Переносы строк сохраняются как есть"
                  sx={{ mb: 2 }}
                />
                <Grid container spacing={2} sx={{ mb: 1 }}>
                  <Grid item xs={12} sm={7}>
                    <TextField
                      label="Ссылка (необязательно)"
                      value={annForm.linkUrl}
                      onChange={(e) => setAnnForm({ ...annForm, linkUrl: e.target.value })}
                      fullWidth
                      placeholder="https://t.me/safepermoon"
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      label="Текст кнопки-ссылки"
                      value={annForm.linkText}
                      onChange={(e) => setAnnForm({ ...annForm, linkText: e.target.value })}
                      fullWidth
                      placeholder="Подробнее"
                    />
                  </Grid>
                </Grid>

                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={annForm.enabled}
                        onChange={(e) => setAnnForm({ ...annForm, enabled: e.target.checked })}
                      />
                    }
                    label="Показывать уведомление"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={annForm.testOnly}
                        color="warning"
                        onChange={(e) => setAnnForm({ ...annForm, testOnly: e.target.checked })}
                      />
                    }
                    label="Тестовый режим — видишь только ты (592457038)"
                  />
                  {annForm.enabled && !annForm.testOnly && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Тестовый режим выключен — после сохранения уведомление увидят ВСЕ игроки!
                    </Alert>
                  )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<Campaign />}
                    onClick={saveAnnouncement}
                    disabled={annSaving}
                  >
                    {annSaving ? 'Сохраняю…' : 'Сохранить и опубликовать'}
                  </Button>
                  {annMeta && (
                    <Typography variant="caption" color="text.secondary">
                      v{annMeta.version}
                      {annMeta.updatedAt ? ` · обновлено ${new Date(annMeta.updatedAt).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' })} МСК` : ''}
                    </Typography>
                  )}
                </Box>
              </Paper>
            )}

            {/* 👤 ЛИЧНОЕ УВЕДОМЛЕНИЕ ОДНОМУ ИГРОКУ */}
            <Paper sx={{ p: { xs: 1.5, sm: 3 }, mt: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                👤 Уведомление одному игроку
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Та же модалка в лобби, но увидит только выбранный игрок — при
                следующем заходе в лобби. Личное показывается раньше общего.
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  label="Игрок: @username, telegram_id или id"
                  value={persQuery}
                  onChange={(e) => setPersQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') searchPersonalUser(); }}
                  size="small"
                  fullWidth
                />
                <Button variant="outlined" onClick={searchPersonalUser}>Найти</Button>
              </Box>

              {persFound.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  {persFound.map((u) => (
                    <Chip
                      key={u.id}
                      sx={{ mr: 1, mb: 1 }}
                      color={persUser?.id === u.id ? 'primary' : 'default'}
                      variant={persUser?.id === u.id ? 'filled' : 'outlined'}
                      label={`${u.username ? '@' + u.username : (u.first_name || 'без имени')} · id ${u.id}`}
                      onClick={() => setPersUser(u)}
                    />
                  ))}
                </Box>
              )}

              {persUser && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Получатель: {persUser.username ? `@${persUser.username}` : (persUser.first_name || `id ${persUser.id}`)}
                  {' '}(tg {persUser.telegram_id})
                </Alert>
              )}

              <TextField
                label="Заголовок"
                value={persForm.title}
                onChange={(e) => setPersForm({ ...persForm, title: e.target.value })}
                fullWidth
                sx={{ mb: 2 }}
              />
              <TextField
                label="Текст"
                value={persForm.body}
                onChange={(e) => setPersForm({ ...persForm, body: e.target.value })}
                fullWidth
                multiline
                minRows={3}
                sx={{ mb: 2 }}
              />
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={7}>
                  <TextField
                    label="Ссылка (необязательно)"
                    value={persForm.linkUrl}
                    onChange={(e) => setPersForm({ ...persForm, linkUrl: e.target.value })}
                    fullWidth
                    placeholder="https://t.me/moonraid_help_bot"
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField
                    label="Текст кнопки-ссылки"
                    value={persForm.linkText}
                    onChange={(e) => setPersForm({ ...persForm, linkText: e.target.value })}
                    fullWidth
                    placeholder="Подробнее"
                  />
                </Grid>
              </Grid>

              <Button
                variant="contained"
                startIcon={<Campaign />}
                onClick={sendPersonalNotice}
                disabled={persSending || !persUser}
              >
                {persSending ? 'Отправляю…' : 'Отправить игроку'}
              </Button>

              {persList.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Отправленные личные
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Когда (МСК)</TableCell>
                          <TableCell>Кому</TableCell>
                          <TableCell>Текст</TableCell>
                          <TableCell>Статус</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {persList.map((n) => (
                          <TableRow key={n.id} hover>
                            <TableCell>{fmtAnnTime(n.created_at)}</TableCell>
                            <TableCell>
                              {n.username ? `@${n.username}` : (n.first_name || `id ${n.telegram_id}`)}
                            </TableCell>
                            <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {n.title || n.body || '—'}
                            </TableCell>
                            <TableCell>
                              {n.acked_at
                                ? `✅ ОК ${fmtAnnTime(n.acked_at)}`
                                : n.seen_at
                                  ? `👀 показано ${fmtAnnTime(n.seen_at)}`
                                  : '⏳ ждёт захода'}
                            </TableCell>
                            <TableCell padding="none">
                              {!n.acked_at && (
                                <Button size="small" color="error" onClick={() => cancelPersonalNotice(n.id)}>
                                  Отозвать
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {persTotal > 10 && (
                    <Pagination
                      size="small"
                      sx={{ mt: 2 }}
                      count={Math.ceil(persTotal / 10)}
                      page={persPage + 1}
                      onChange={(e, p) => loadPersonalNotices(p - 1)}
                    />
                  )}
                </>
              )}
            </Paper>

            {/* 📬 История рассылок: охват + нажатия OK */}
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                📬 История рассылок
              </Typography>
              {annBroadcasts.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Пока не было ни одной публикации
                </Typography>
              ) : (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>№</TableCell>
                          <TableCell>Когда (МСК)</TableCell>
                          <TableCell>Текст</TableCell>
                          <TableCell>Режим</TableCell>
                          <TableCell align="right">Показано</TableCell>
                          <TableCell align="right">ОК</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {annBroadcasts.map((b) => (
                          <React.Fragment key={b.version}>
                            <TableRow hover>
                              <TableCell>v{b.version}</TableCell>
                              <TableCell>{fmtAnnTime(b.published_at)}</TableCell>
                              <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {b.title || b.body || '—'}
                              </TableCell>
                              <TableCell>{b.test_only ? '🧪 тест' : '🟢 все'}</TableCell>
                              <TableCell align="right">{b.seen_count}</TableCell>
                              <TableCell align="right">
                                <b>{b.ack_count}</b>
                                {b.seen_count > 0 && (
                                  <Typography component="span" variant="caption" color="text.secondary">
                                    {' '}({Math.round((b.ack_count / b.seen_count) * 100)}%)
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell padding="none">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    if (annAcksVersion === b.version) setAnnAcksVersion(null);
                                    else loadAnnAcks(b.version, 0);
                                  }}
                                >
                                  {annAcksVersion === b.version ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                </IconButton>
                              </TableCell>
                            </TableRow>
                            {annAcksVersion === b.version && (
                              <TableRow>
                                <TableCell colSpan={7} sx={{ py: 1.5, bgcolor: 'rgba(255,255,255,0.03)' }}>
                                  {annAcks.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                      OK ещё никто не нажал
                                    </Typography>
                                  ) : (
                                    <>
                                      {annAcks.map((a) => (
                                        <Box key={a.user_id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4, px: 1 }}>
                                          <Typography variant="body2">
                                            {a.username ? `@${a.username}` : (a.first_name || `id ${a.telegram_id}`)}
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            {fmtAnnTime(a.acked_at)}
                                          </Typography>
                                        </Box>
                                      ))}
                                      {annAcksTotal > 10 && (
                                        <Pagination
                                          size="small"
                                          sx={{ mt: 1 }}
                                          count={Math.ceil(annAcksTotal / 10)}
                                          page={annAcksPage + 1}
                                          onChange={(e, p) => loadAnnAcks(b.version, p - 1)}
                                        />
                                      )}
                                    </>
                                  )}
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {annBroadcastsTotal > 10 && (
                    <Pagination
                      size="small"
                      sx={{ mt: 2 }}
                      count={Math.ceil(annBroadcastsTotal / 10)}
                      page={annBroadcastsPage + 1}
                      onChange={(e, p) => loadAnnBroadcasts(p - 1)}
                    />
                  )}
                </>
              )}
            </Paper>
          </Box>
        </TabPanel>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <SlotPlayerGambleMenu
        open={gambleMenuOpen}
        onClose={() => setGambleMenuOpen(false)}
        userId={gambleMenuUserId}
      />
    </Box>
  );
};

export default SlotsSettings;
